/* assets/system-finder.js
   Static steps + recommendation logic. Product data comes from snippets/finder-data.liquid.
   A recommendation is a setup: a main system for outdoor areas, plus standalone products
   (indoor Wi-Fi cameras, a video doorbell) for the areas they suit better. */
(function () {
  'use strict';

  /* ================= Static content ================= */
  const PROPS = {
    home: { label: 'House', desc: 'Detached, semi-detached, townhouse or vacation home', icon: 'house' },
    rental: { label: 'Condo or apartment', desc: 'Setups that need no drilling or wiring', icon: 'condo', wireFree: true, scene: 'condo' }
  };
  const INSTALL = {
    wirefree: { label: 'Wire-free, battery powered', short: 'Wire-free', desc: 'Battery cameras with no cables at all. Mount them anywhere and recharge every few months.', icon: 'battery' },
    wifi: { label: 'Wi-Fi, no network cables', short: 'Wi-Fi', desc: 'Cameras connect to your Wi-Fi. Some run on battery, others plug into a nearby power outlet.', icon: 'wifi' },
    new: { label: 'I can run new cables', short: 'New cables', desc: 'One cable per camera to a recorder: Ethernet for an IP system or coaxial for an analog system. The most reliable picture for 4 or more cameras.', icon: 'ethernet' },
    coax: { label: 'I have existing camera cables', short: 'Existing cables', desc: 'Replace an older system and reuse the round coaxial cables already in your walls.', icon: 'coax' },
    hybrid: { label: 'Mix of wired and Wi-Fi', short: 'Wired + Wi-Fi', desc: 'Wired cameras where you can run cables, Wi-Fi cameras for spots that are hard to reach, like a garage or side yard.', icon: 'hybrid' },
    unsure: { label: 'I\u2019m not sure', short: 'Install: not sure', desc: 'We\u2019ll choose based on your property and the areas you picked.', icon: 'question' }
  };
  const PRIOS = {
    res: { label: 'See every detail', desc: '4K or 2K+ resolution', short: '4K or 2K+ resolution', icon: 'res' },
    lens: { label: 'Cover more areas', desc: 'Pan-tilt or dual-lens cameras', short: 'pan-tilt or dual lens', icon: 'lens' },
    night: { label: 'See clearly at night', desc: 'Color night vision', short: 'color night vision', icon: 'moon' },
    detect: { label: 'Get smarter alerts', desc: 'Smart AI detections', short: 'smart alerts', icon: 'person' },
    deter: { label: 'Scare away intruders', desc: 'Active deterrence with lights and sirens', short: 'active deterrence', icon: 'siren' },
    audio: { label: 'Hear and talk remotely', desc: 'Two-way talk, listen-in or instant call', short: 'two-way talk', icon: 'talk' },
    cloud: { label: 'Find and save footage', desc: 'AI Smart Search+ and cloud backup', short: 'cloud backup', icon: 'cloud' },
    continuous: { label: 'Record around the clock', desc: '24/7 recording', short: '24/7 recording', icon: 'record' }
  };
  /* Detection values from product.motion_detection. Add 'mask' here to offer it as an option. */
  const DETECTIONS = { person: 'People', vehicle: 'Vehicles', package: 'Packages', face: 'Faces', animal: 'Animals', pet: 'Pets (cats and dogs)' };
  /* Budget is a preference, not a price range: it sets how much price counts against features */
  const BUDGET = {
    budget: { label: 'Budget-friendly', desc: 'The lowest price that still covers every area you picked', icon: 'money' },
    premium: { label: 'Best performance', desc: 'The best cameras and features for each area', icon: 'star' },
    skip: { label: 'I don’t have a budget in mind', short: 'No budget', desc: 'A balance of price and features', icon: 'question' }
  };
  const STEPS = ['Property', 'Areas', 'Priorities', 'Install', 'Budget'];

  /* Line-item properties sent with every product. Keys starting with "_" are hidden from
     shoppers at checkout but stay on the order. Edit freely. */
  const lineProperties = (line, setupName) => {
    const p = { _source: 'System finder', _setup: setupName };
    if (line.zones && line.zones.length) p['Covers'] = zoneLabels(line.zones, false).join(', ');
    return p;
  };

  /* Favour products on this app (jasper.remote-connectivity). Set item to 0 and setup to 0 to turn off.
     item: added when picking between products for one role (features score about 10 each, weatherproof 40).
     setup: added to a whole setup in proportion to how much of it is on the app
     (an area left uncovered costs 40, a weather issue 30). */
  const PREFERRED_APP = 'connect';
  const PREFERRED_APP_BOOST = { item: 8, setup: 20 };

  /* A typical full setup price in cents. Price is scored relative to this. */
  const PRICE_SCALE = { CA: 70000, US: 70000 };
  /* How much price counts for each budget choice (a setup at PRICE_SCALE loses this many points).
     For comparison: an uncovered area costs 40 and a priority feature on every camera is worth 10 to 30. */
  const PRICE_WEIGHT = { budget: 45, premium: 1.5, skip: 6 };
  /* Best performance: points per unit of camera quality (see itemQuality) */
  const QUALITY_WEIGHT = 10;
  /* Most complementary products to suggest as add-ons on the results step */
  const MAX_COMPS = 4;
  /* Most of one add-on a shopper can add on the results step */
  const ADDON_MAX = 9;

  /* product.type -> role. Order matters: "Wi-Fi Doorbell" must hit doorbell before wifi,
     "Indoor Wi-Fi Camera" must hit indoor before wifi. */
  const TYPE_RULES = [
    [/doorbell/i, 'doorbell'],
    [/flood\s*light/i, 'floodlight'],
    [/hard\s*drive|\bhdd\b|storage/i, 'drive'],
    [/indoor/i, 'indoor'],
    [/^(?!.*(system|kit|bundle)).*\b(nvrs?|network video recorders?)\b/i, 'nvr'],
    [/^(?!.*(system|kit|bundle)).*\bip\s*(security\s*)?cameras?\b/i, 'ipcam'],
    [/\bnvr\b|\bip\b|poe/i, 'ip'],
    [/\bdvr\b|analog|coax|bnc/i, 'analog'],
    [/wi-?fi|wire-?free|battery/i, 'wifi']
  ];
  const SYSTEM_TYPES = ['wifi', 'analog', 'ip'];
  const STANDALONE_ROLES = ['indoor', 'doorbell', 'floodlight', 'ipcam', 'nvr'];

  /* Area pins and camera views, in a 600 x 420 coordinate space.
     kind: 'doorbell' (covered by a video doorbell), 'indoor' (indoor camera), default outdoor.
     far: usually hard to run a cable to, so a wired + Wi-Fi mix puts a Wi-Fi camera there.
     wireless: only ever a Wi-Fi or battery camera, even in a wired setup (e.g. a detached shed).
     Open the page with ?calibrate and click the photo to log new coordinates. */
  const ZONES = {
    /* Mapped to the aerial house photo (bungalow with attached garage on the right, patio and
       shed out back): house x 128-400, y 152-330; garage x 400-504, y 188-334. */
    house: [
      /* Doorbell at the front door, looking down the front walk */
      { id: 'front-door', label: 'Front door', pin: [265, 362], cam: [262, 324], a: 90, len: 52, sp: 70, kind: 'doorbell' },
      /* Over the garage door, looking down the driveway */
      { id: 'driveway', label: 'Driveway', pin: [450, 372], cam: [450, 334], a: 90, len: 62, sp: 72, flood: true, weather: true },
      /* Front-left corner of the house, looking over the front lawn */
      { id: 'front-yard', label: 'Front yard', pin: [96, 368], cam: [130, 330], a: 115, len: 62, sp: 85, weather: true },
      /* Back wall above the patio, looking over the back lawn */
      { id: 'backyard', label: 'Backyard', pin: [176, 70], cam: [282, 152], a: -90, len: 95, sp: 110, flood: true, weather: true },
      /* Mounted on the front of the shed, looking back across the lawn toward the house.
         wireless: always a Wi-Fi or battery camera, never wired to the recorder */
      { id: 'shed', label: 'Shed', pin: [496, 52], cam: [496, 86], a: 146, len: 115, sp: 50, weather: true, far: true, wireless: true },
      /* Back-left corner of the house, looking down the side yard toward the street */
      { id: 'side-yard', label: 'Side yard', pin: [70, 228], cam: [128, 168], a: 118, len: 112, sp: 40, weather: true, far: true },
      /* Inside the garage, back corner, looking over the car toward the garage door */
      { id: 'garage', label: 'Garage', pin: [438, 214], cam: [496, 196], a: 115, len: 120, sp: 55, far: true },
      /* Living room corner, looking across the room to the front door */
      { id: 'indoor', label: 'Inside the home', pin: [178, 296], cam: [140, 266], a: 25, len: 100, sp: 50, kind: 'indoor' }
    ],
    /* Condo or apartment: one unit, its hallway door and balcony */
    condo: [
      { id: 'unit-door', label: 'Front door', pin: [101, 354], cam: [133, 308], a: 90, len: 60, sp: 80, kind: 'doorbell' },
      { id: 'entryway', label: 'Entryway', pin: [115, 251], cam: [179, 179], a: 122, len: 107, sp: 50, kind: 'indoor' },
      { id: 'living-room', label: 'Living room', pin: [336, 181], cam: [312, 44], a: 64, len: 189, sp: 48, kind: 'indoor' },
      { id: 'bedroom', label: 'Bedroom', pin: [129, 96], cam: [212, 139], a: -152, len: 133, sp: 50, kind: 'indoor' },
      { id: 'balcony', label: 'Balcony', pin: [505, 255], cam: [523, 41], a: 90, len: 241, sp: 22, weather: true }
    ]
  };

  /* Drawn fallback plans, used when no photo is set in the theme editor */
  const SCENES = {
    condo: '<rect class="pave" x="0" y="340" width="600" height="80"/><text class="lbl" x="560" y="410" text-anchor="end">Hallway</text>' +
      '<rect class="lot" x="470" y="20" width="130" height="310"/><rect class="pave" x="460" y="50" width="110" height="260" rx="3"/><line class="wall" x1="570" y1="50" x2="570" y2="310"/><text class="lbl" x="515" y="300" text-anchor="middle">Balcony</text>' +
      '<rect class="bldg" x="30" y="30" width="430" height="306"/>' +
      '<line class="wall" x1="220" y1="30" x2="220" y2="170"/><line class="wall" x1="30" y1="170" x2="150" y2="170"/><line class="wall" x1="190" y1="170" x2="220" y2="170"/>' +
      '<line class="wall" x1="220" y1="120" x2="300" y2="120"/><line class="wall" x1="300" y1="30" x2="300" y2="120"/>' +
      '<rect class="fix" x="60" y="56" width="100" height="70" rx="4"/><rect class="fix" x="56" y="236" width="120" height="18" rx="2"/><rect class="fix" x="300" y="246" width="120" height="30" rx="6"/><circle class="fix" cx="250" cy="250" r="18"/>' +
      '<rect class="door" x="88" y="332" width="32" height="6"/><rect class="door" x="456" y="140" width="6" height="90"/>',
    /* Same layout as the house photo, so the area pins line up with either */
    house: '<rect class="lot" x="10" y="10" width="580" height="384" rx="4"/><rect class="street" x="0" y="398" width="600" height="22"/>' +
      '<circle class="tree" cx="40" cy="40" r="26"/><circle class="tree" cx="568" cy="40" r="24"/><circle class="tree" cx="56" cy="364" r="20"/><circle class="tree" cx="562" cy="366" r="20"/>' +
      '<rect class="bldg" x="470" y="20" width="54" height="66"/><text class="lbl" x="497" y="100" text-anchor="middle">Shed</text>' +
      '<rect class="pave" x="208" y="113" width="146" height="39" rx="2"/><rect class="pave" x="410" y="336" width="78" height="60"/><rect class="pave" x="252" y="336" width="26" height="60"/>' +
      '<rect class="bldg" x="128" y="152" width="272" height="178"/><rect class="bldg" x="400" y="188" width="104" height="146"/>' +
      '<line class="wall" x1="128" y1="222" x2="400" y2="222"/><line class="wall" x1="128" y1="258" x2="282" y2="258"/><line class="wall" x1="282" y1="258" x2="282" y2="330"/><line class="wall" x1="244" y1="152" x2="244" y2="222"/><line class="wall" x1="334" y1="152" x2="334" y2="222"/>' +
      '<rect class="door" x="254" y="326" width="22" height="6"/><rect class="door" x="414" y="330" width="72" height="6"/><text class="lbl" x="560" y="414" text-anchor="end">Street</text>'
  };

  const IP = {
    house: '<path d="M3 11 12 4l9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/>',
    condo: '<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M11 21v-3h2v3"/>',
    store: '<path d="M3 9 4.5 4h15L21 9M4 9v11h16V9M3 9h18M9 20v-6h6v6"/>',
    wifi: '<path d="M2 9a15 15 0 0 1 20 0M5.5 12.5a10 10 0 0 1 13 0M9 16a5 5 0 0 1 6 0M12 19.5h.01"/>',
    coax: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.5"/><path d="M12 12h.01"/>',
    ethernet: '<rect x="4" y="5" width="16" height="13" rx="1"/><path d="M8 18v3h8v-3M8 9v2M11 9v2M13 9v2M16 9v2"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5h.01"/>',
    question: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.6V14M12 17h.01"/>',
    face: '<path d="M4 8V5h3M17 5h3v3M20 16v3h-3M7 19H4v-3"/><circle cx="12" cy="10.5" r="2.8"/><path d="M8 17a4 4 0 0 1 8 0"/>',
    res: '<rect x="3" y="5" width="18" height="14" rx="1.5"/><path d="M7 9v3h3M9 9v6M13 9v6M17 9l-3 3 3 3"/>',
    lens: '<rect x="3" y="7" width="18" height="10" rx="2"/><circle cx="8.5" cy="12" r="2.5"/><circle cx="15.5" cy="12" r="2.5"/><path d="M8 4h8M19 4l2 1.5L19 7"/>',
    moon: '<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"/>',
    siren: '<path d="M7 18v-5a5 5 0 0 1 10 0v5M5 18h14v3H5zM12 3v2M4.2 6.2l1.4 1.4M19.8 6.2l-1.4 1.4"/>',
    talk: '<path d="M4 5h16v11H9l-5 4z"/><path d="M8 10h8M8 13h5"/>',
    person: '<circle cx="12" cy="7.5" r="3.5"/><path d="M5 21a7 7 0 0 1 14 0"/>',
    drive: '<path d="M3 13 6 5h12l3 8M3 13h18v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM17 16.5h.01"/>',
    cloud: '<path d="M7 18h10a4 4 0 0 0 .6-7.96A6 6 0 0 0 6.1 9.6 4.2 4.2 0 0 0 7 18z"/>',
    record: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5" fill="currentColor"/>',
    money: '<rect x="3" y="6" width="18" height="12" rx="1"/><circle cx="12" cy="12" r="2.5"/>',
    star: '<path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z"/>',
    battery: '<rect x="2" y="7" width="17" height="10" rx="2"/><path d="M22 11v2M6 10.5v3M10 10.5v3M14 10.5v3"/>',
    hybrid: '<path d="M2 8.5a13 13 0 0 1 13 0M5 11.5a8 8 0 0 1 7 0M8.5 14.5h.01"/><rect x="14" y="12" width="8" height="7" rx="1"/><path d="M16 19v2.5h4V19M16.5 14.5v1.5M19.5 14.5v1.5"/>',
    back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
    sliders: '<path d="M4 8h9M17 8h3M4 16h3M11 16h9"/><circle cx="15" cy="8" r="2"/><circle cx="9" cy="16" r="2"/>',
    restart: '<path d="M4 12a8 8 0 1 0 2.3-5.6M4 4v4h4"/>'
  };
  const icon = k => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${IP[k] || ''}</svg>`;
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const skuTag = it => (it && it.sku ? ` <span class="sf-sku">SKU: ${esc(it.sku)}</span>` : '');
  const cap = t => t.charAt(0).toUpperCase() + t.slice(1);
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const joinAnd = a => a.length <= 1 ? (a[0] || '') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`;
  /* Area names with camera counts, e.g. ["front yard", "backyard (2)"]. An area with
     several cameras appears in zone lists once per camera; this groups them back together. */
  const zoneLabels = (zs, lower = true) => {
    const m = new Map();
    (zs || []).forEach(z => { const e = m.get(z.id) || { z, n: 0 }; e.n++; m.set(z.id, e); });
    return [...m.values()].map(({ z, n }) => `${lower ? z.label.toLowerCase() : z.label}${n > 1 ? ` (${n})` : ''}`);
  };
  /* Most cameras a shopper can ask for in one area */
  const ZONE_MAX = 6;

  /* ================= Normalize Liquid data ================= */
  const classify = t => (TYPE_RULES.find(([re]) => re.test(t || '')) || [])[1] || null;
  const num = v => { const m = String(v ?? '').match(/\d+/); return m ? +m[0] : 0; };
  const list = v => (Array.isArray(v) ? v : String(v ?? '').split(/[,;|]/)).map(x => String(x).trim()).filter(Boolean);
  const NO = /^(false|no|none|n\/a|-)$/i;
  const normRes = v => {
    const s = String(v ?? '').toLowerCase();
    if (/4k|8\s*mp|2160/.test(s)) return '4K';
    if (/2k|4\s*mp|5\s*mp|1440/.test(s)) return '2K';
    if (/1080|2\s*mp/.test(s)) return '1080p';
    return String(v ?? '');
  };
  const normDetect = v => [...new Set(list(v).map(x => {
    const s = x.toLowerCase();
    if (/person|people|human/.test(s)) return 'person';
    if (/vehicle|car/.test(s)) return 'vehicle';
    if (/package|parcel/.test(s)) return 'package';
    if (/mask/.test(s)) return 'mask';
    if (/face/.test(s)) return 'face';
    if (/\bpets?\b|\bcats?\b|\bdogs?\b/.test(s)) return 'pet';
    if (/animal/.test(s)) return 'animal';
    return null;
  }).filter(Boolean))];
  const normAudio = v => {
    const s = list(v).join(' ').toLowerCase();
    if (/\b(2|two)\s*-?\s*way\b|instant\s*call/.test(s)) return '2way';
    if (/listen/.test(s)) return 'listen';
    return null;
  };
  /* jasper.power-source: battery, plug-in, hardwired (doorbell wiring, light fixture), or battery-or-wired */
  const powerOf = t => {
    const bat = /battery|rechargeable/i.test(t);
    const hard = /hard\s*-?wire|\bwired\b|doorbell wiring|existing wiring|junction|fixture/i.test(t);
    const plug = /plug|outlet|adapter|\bac\b|usb/i.test(t);
    if (bat && hard) return 'flex';
    if (bat) return 'battery';
    if (hard) return 'hardwired';
    if (plug) return 'plug';
    if (/solar/i.test(t)) return 'battery';
    return null;
  };
  /* App keys for matching. "Home" was renamed "Classic", so both count as the same app.
     appNames keeps the merchant's wording for display. */
  const appKey = s => {
    const t = s.toLowerCase();
    if (/connect/.test(t)) return 'connect';
    if (/home|classic/.test(t)) return 'classic';
    if (/\bpro\b/.test(t)) return 'pro';
    if (/cirrus/.test(t)) return 'cirrus';
    if (/cloud/.test(t)) return 'cloud';
    return t;
  };
  const appsOf = v => {
    const names = list(v).filter(x => !NO.test(x));
    return { apps: [...new Set(names.map(appKey))], appNames: names };
  };
  /* Feature fields shared by every product (from snippets/finder-attrs.liquid) */
  const feats = o => ({
    color: /colou?r/i.test(list(o.night_vision).join(' ')),
    deterList: list(o.deterrence).filter(x => !NO.test(x) && !/^true$/i.test(x)),
    deter: o.deterrence === true || list(o.deterrence).some(x => !NO.test(x)),
    detect: normDetect(o.motion_detection),
    audio: normAudio(o.audio),
    cloud: o.cloud === true,
    power: powerOf(list(o.power_source).join(' ')),
    componentOnly: /^(yes|true)$/i.test(list(o.component_only).join(' ').trim()),
    /* product.camera-features: pan and tilt, dual lens, and anything else listed.
       The product title counts too, e.g. "4K Dual Lens Wi-Fi Camera" or "Pan-Tilt Camera". */
    camFeatures: [...new Set([
      ...list(o.camera_features).map(x => /pan|tilt|ptz/i.test(x) ? 'Pan and tilt' : /dual|two[\s-]*lens|2[\s-]*lens/i.test(x) ? 'Dual lens' : x.charAt(0).toUpperCase() + x.slice(1)).filter(x => !NO.test(x)),
      ...(/pan[\s-]*(and|&|\/)?[\s-]*tilt|\bptz\b/i.test(o.name || '') ? ['Pan and tilt'] : []),
      ...(/dual[\s-]*lens/i.test(o.name || '') ? ['Dual lens'] : [])
    ])],
    /* jasper.resolution and facets.resolution: '4K', '2K' (2K+ and 4MP/5MP count as 2K) or '1080p'.
       Either field can supply it; the higher of the two wins. */
    res: normRes([...list(o.resolution), ...list(o.resolution_facet)].join(' ')),
    /* jasper.remote-connectivity: the app(s) the product works with */
    ...appsOf(o.remote_connectivity),
    ip: (list(o.environmental_rating).join(' ').match(/IP\s?6[6-9]/i) || [''])[0].replace(/\s/, '').toUpperCase(),
    family: list(o.family_series).join(' '),
    series: ((list(o.family_series).join(' ').match(/\b([a-z0-9]+)\s+series\b/i) || [])[1] || '').toUpperCase()
  });
  /* Weatherproof = IP66 or better on jasper.environmental-rating */
  const weatherproof = it => /^IP6[6-9]$/.test(it.ip || '');
  /* "Cover more areas": pan-tilt or dual-lens cameras */
  const hasLens = it => (it.camFeatures || []).some(f => f === 'Pan and tilt' || f === 'Dual lens');
  /* jasper.family-series "Indoor Wi-Fi Camera" marks indoor products; product type is the fallback */
  const roleOf = o => {
    const fam = list(o.family_series).join(' ');
    /* Doorbells and floodlights are recognised by type, title or family series, so a
       "Wi-Fi Cameras" product titled "Floodlight Camera" is never used as a general camera.
       Kits ("System with 4 Floodlight Cameras") are left to the rules below. */
    const text = `${o.product_type || ''} ${o.name || ''} ${fam}`;
    if (!/system|kit|bundle/i.test(text)) {
      if (/doorbell/i.test(text)) return 'doorbell';
      if (/flood\s*-?light/i.test(text)) return 'floodlight';
    }
    if (/indoor\s*wi-?fi\s*camera/i.test(fam)) return 'indoor';
    if (/\bnvr\b/i.test(fam) && !/system|kit|bundle/i.test(fam)) return 'nvr';
    if (/\bip\s*(security\s*)?camera/i.test(fam)) return 'ipcam';
    return classify(o.product_type);
  };

  /* Shopify Bundles: Liquid can't see a bundle's components, so a bundle without a
     "Number of cameras" option would count as 1 camera. The Storefront API (tokenless) returns
     each bundle variant's components and quantities; cameras among them are counted here.
     Returns { variantId: cameraCount } for bundles only. */
  const SF_API = '/api/2026-01/graphql.json';
  const COMPONENT_CAMERA = /camera|doorbell|flood\s*-?light/i;
  const COMPONENT_NOT_CAMERA = /\b(nvr|dvr|recorder|hard\s*drive|hdd|cables?|mounts?|brackets?|junction\s*box)\b/i;
  async function fetchBundleCams(ids) {
    const out = {};
    const query = 'query($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id components(first:25){nodes{quantity productVariant{product{title productType}}}}}}}';
    const chunks = [];
    for (let i = 0; i < ids.length; i += 20) chunks.push(ids.slice(i, i + 20));
    await Promise.all(chunks.map(async chunk => {
      const res = await fetch(SF_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ query, variables: { ids: chunk.map(id => `gid://shopify/ProductVariant/${id}`) } })
      });
      if (!res.ok) return;
      const json = await res.json();
      for (const n of (json.data && json.data.nodes) || []) {
        const parts = (n && n.components && n.components.nodes) || [];
        const cams = parts.reduce((t, p) => {
          const pr = (p.productVariant && p.productVariant.product) || {};
          const txt = `${pr.productType || ''} ${pr.title || ''}`;
          return t + (COMPONENT_CAMERA.test(txt) && !COMPONENT_NOT_CAMERA.test(txt) ? p.quantity : 0);
        }, 0);
        if (cams) out[n.id.split('/').pop()] = cams;
      }
    }));
    return out;
  }
  /* Variant ids worth checking: system and Wi-Fi pack variants with no camera-count option */
  const bundleCandidates = DATA => (DATA.systems || [])
    .filter(p => SYSTEM_TYPES.includes(roleOf(p)))
    .flatMap(p => (p.variants || []).filter(v => !num(v.cameras_option)).map(v => String(v.variant_id)));

  /* camOverride: { variantId: cameras } from fetchBundleCams */
  function buildCatalog(DATA, camOverride = {}) {
    const camsOf = v => camOverride[v.variant_id] || num(v.cameras_option) || 1;
    const systems = [], standalone = { indoor: [], doorbell: [], floodlight: [], ipcam: [], nvr: [] };
    const asItem = (c, role) => ({ id: String(c.variant_id), variant_id: c.variant_id, product_id: c.product_id, name: c.name, sku: c.sku || '', ptype: c.product_type, url: c.url, image: c.image || null, price: c.price, role, ...feats(c) });

    for (const prod of DATA.systems || []) {
      const role = roleOf(prod);
      const variants = prod.variants || [];
      if (!variants.length) continue;

      const comp = (prod.complementary || []).filter(c => c.available).map(c => ({ c, role: roleOf(c) }));
      const pickComp = r => { const f = comp.find(x => x.role === r); return f ? asItem(f.c, r) : null; };
      const addons = { camera: pickComp(role === 'ip' ? 'ipcam' : role), doorbell: pickComp('doorbell'), floodlight: pickComp('floodlight'), drive: pickComp('drive'), indoor: pickComp('indoor') };
      /* Every complementary product, suggested as optional add-ons on the results step */
      const comps = comp.map(x => asItem(x.c, x.role));

      /* Every available variant, so shoppers can switch color or pack size on the results step */
      const camCounts = new Set(variants.map(camsOf));
      const nameOf = v => {
        const cams = camsOf(v);
        return camCounts.size > 1 && v.cameras_option && cams > 1 ? `${prod.name}, ${/^\s*\d+\s*$/.test(v.cameras_option) ? `${cams} cameras` : v.cameras_option}` : prod.name;
      };
      const vlist = variants.length > 1 ? variants.map(v => ({
        id: String(v.variant_id), variant_id: v.variant_id, sku: v.sku || '', price: v.price, url: v.url, image: v.image,
        title: v.title, opts: v.options || [], cams: camsOf(v), name: nameOf(v)
      })) : null;
      const withVariants = it => (vlist ? Object.assign(it, { variants: vlist, optNames: prod.options || [] }) : it);

      if (STANDALONE_ROLES.includes(role)) {
        const v = variants.reduce((a, b) => (b.price < a.price ? b : a));
        const item = withVariants(asItem({ ...prod, ...v, image: v.image }, role));
        item.comps = comps;
        if (role === 'nvr') { item.ch = num(prod.channels); item.addons = addons; if (!item.ch) continue; }
        standalone[role].push(item);
        continue;
      }
      if (!SYSTEM_TYPES.includes(role)) continue;
      /* Wired kits must include a recorder: channels set, or named as a system/kit */
      const hasRecorder = num(prod.channels) > 0 || /system|kit|bundle/i.test(`${prod.product_type} ${prod.name} ${list(prod.family_series).join(' ')}`);
      if (role !== 'wifi' && !hasRecorder) continue;
      const f = feats(prod);

      const byCams = new Map();
      for (const v of variants) {
        const cams = camsOf(v);
        const cur = byCams.get(cams);
        if (!cur || v.price < cur.price) byCams.set(cams, v);
      }
      for (const [cams, v] of byCams) {
        systems.push(withVariants({
          id: String(v.variant_id), variant_id: v.variant_id, product_id: prod.product_id, role: 'system', ptype: prod.product_type,
          name: nameOf(v),
          sku: v.sku || '', url: v.url, image: v.image, price: v.price, type: role, cams,
          ch: num(prod.channels), addons, comps, ...f
        }));
      }
    }
    return { systems, standalone };
  }

  /* ================= App ================= */
  function init(root) {
    if (root.dataset.sfReady) return;
    const dataEl = root.querySelector('script[data-finder-data]');
    if (!dataEl) return;
    root.dataset.sfReady = '1';

    let DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { console.error('System finder: invalid data', e); return; }
    const MARKET = DATA.market === 'US' ? 'US' : 'CA';
    let { systems: P, standalone: ACC } = buildCatalog(DATA);
    const IMG = DATA.images || {};
    /* Install step pictures, keyed like INSTALL (set in snippets/finder-data.liquid) */
    const INSTALL_IMG = DATA.install_images || {};
    const INTRO = {
      eyebrow: root.dataset.eyebrow || '',
      title: root.dataset.title || 'Find the right security system for your property',
      description: root.dataset.description || ''
    };
    /* Note under the checkout button, set in the theme editor (e.g. free shipping threshold) */
    const SUMMARY_NOTE = root.dataset.summaryNote || '';

    const $main = root.querySelector('[data-sf-main]');
    const $rail = root.querySelector('[data-sf-rail]');
    const $bar = root.querySelector('[data-sf-bar]');
    const $toast = root.querySelector('[data-sf-toast]');

    const cur = DATA.currency || (MARKET === 'US' ? 'USD' : 'CAD');
    const money = new Intl.NumberFormat(DATA.locale || undefined, { style: 'currency', currency: cur });
    const money0 = new Intl.NumberFormat(DATA.locale || undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 });
    const fmt = cents => (cents % 100 === 0 ? money0 : money).format(cents / 100);

    const SCALE = PRICE_SCALE[MARKET];
    /* Price difference against the current setup, e.g. "+$100 CAD" or "−$100 CAD" */
    const diffText = d => `${d >= 0 ? '+' : '−'}${fmt(Math.abs(d))} ${cur}`;

    /* counts: cameras wanted per area id (1 when missing) */
    const state = { step: 0, property: null, zones: [], counts: {}, install: null, priorities: [], detections: [], budget: null, pick: null, sel: {}, qty: {}, choice: {}, variant: {} };
    let lastStep = -1;

    const sceneOf = () => (state.property && PROPS[state.property].scene) || 'house';
    const zonesOf = () => ZONES[sceneOf()];
    const zoneById = id => Object.values(ZONES).flat().find(z => z.id === id);
    const clearPick = () => { state.pick = null; state.sel = {}; state.qty = {}; state.choice = {}; state.variant = {}; };
    let hasDoorbellProduct, hasIndoorProduct;
    const setFlags = () => {
      hasDoorbellProduct = ACC.doorbell.length > 0 || P.some(p => p.addons.doorbell);
      hasIndoorProduct = ACC.indoor.length > 0 || P.some(p => p.addons.indoor);
    };
    setFlags();

    /* ================= Setup engine =================
       Every candidate setup is built, scored and ranked. A setup can mix a wired system,
       wire-free cameras, floodlight cameras, indoor cameras and a doorbell. Each picked area
       is assigned to exactly one product, and features are judged area by area. */
    /* "Wired" here means wired to a recorder (IP/PoE or analog), not wired for power.
       Anything labelled Wi-Fi or wire-free connects wirelessly, even if it's hardwired for power,
       so Wi-Fi doorbells and floodlights work in any setup without a recorder. */
    const isWired = it => {
      const t = `${it.ptype || ''} ${it.name || ''} ${it.family || ''}`;
      if (/wi-?fi|wire-?free|wireless/i.test(t)) return false;
      return /poe|\bip\b|nvr|dvr|bnc|coax|ethernet|analog/i.test(t);
    };
    const isBattery = it => !isWired(it) && it.power === 'battery';
    const isPlugIn = it => !isWired(it) && it.power === 'plug';
    /* Wi-Fi cameras on constant power (plug-in or hardwired) can record 24/7 to a microSD card.
       Battery cameras, and ones that may run on battery, record motion clips only. */
    const sd247 = it => !isWired(it) && (it.power === 'plug' || it.power === 'hardwired');
    /* Wire-free install: battery only (battery-or-hardwired counts, it can run on battery) */
    const runsOnBattery = it => !isWired(it) && (it.power === 'battery' || it.power === 'flex');
    /* How a Wi-Fi product gets power, in plain words */
    const isHardwired = it => !isWired(it) && it.power === 'hardwired';
    const HARDWIRE_WHERE = { doorbell: 'your existing doorbell wiring', flood: 'an outdoor light fixture or junction box' };
    const powerText = (it, role) => {
      if (isWired(it)) return '';
      if (it.power === 'battery') return 'battery powered, connects to Wi-Fi';
      if (it.power === 'flex') return `connects to Wi-Fi, runs on battery or ${HARDWIRE_WHERE[role] || 'hardwired power'}`;
      if (it.power === 'hardwired') return `connects to Wi-Fi, powered by ${HARDWIRE_WHERE[role] || 'hardwired power'}`;
      if (it.power === 'plug') return 'connects to Wi-Fi, plugs into a power outlet';
      return 'connects to Wi-Fi, check the power source';
    };
    const ROLE_NAME = { system: 'system cameras', addcam: 'add-on cameras', ipcam: 'IP cameras', nvr: 'NVR', pack: 'Wi-Fi cameras', flood: 'floodlight camera', indoor: 'indoor camera', doorbell: 'doorbell' };

    /* Every area can have several cameras, including the front door (e.g. a doorbell per door) */
    const zoneCount = id => (zoneById(id) ? Math.min(ZONE_MAX, Math.max(1, state.counts[id] || 1)) : 1);
    function splitZones() {
      /* One entry per camera: an area with 2 cameras appears twice, so every product line,
         quantity and price counts it twice */
      const zs = state.zones.map(zoneById).filter(Boolean)
        .flatMap(z => Array.from({ length: zoneCount(z.id) }, (_, i) => (i ? { ...z, copy: i } : z)));
      const D = hasDoorbellProduct ? zs.filter(z => z.kind === 'doorbell') : [];
      const I = zs.filter(z => z.kind === 'indoor');
      const O = zs.filter(z => !D.includes(z) && !I.includes(z));
      return { D, I, O };
    }
    function allowedTypes() {
      if (state.install === 'wifi' || state.install === 'wirefree') return ['wifi'];
      if (state.install === 'coax') return ['analog'];
      if (state.install === 'new' || state.install === 'hybrid') return ['ip', 'analog'];
      if (state.property && PROPS[state.property].wireFree) return ['wifi'];
      return ['wifi', 'ip'];
    }
    const indoorToMain = () => !hasIndoorProduct;

    const onPreferredApp = it => (it.apps || []).includes(PREFERRED_APP);

    /* ----- Item scoring ----- */
    function featureScore(p) {
      let s = 0;
      const pr = state.priorities, det = p.detect || [];
      if (pr.includes('res')) s += p.res === '4K' ? 12 : p.res === '2K' ? 8 : 0;
      if (pr.includes('lens') && hasLens(p)) s += 10;
      if (pr.includes('night') && p.color) s += 10;
      if (pr.includes('deter') && p.deter) s += 14;
      if (pr.includes('detect') && det.length) {
        s += 6 + (state.detections.length ? state.detections.filter(d => det.includes(d)).length * 4 : 4);
      }
      if (pr.includes('audio')) s += p.audio === '2way' ? 10 : p.audio === 'listen' ? 4 : 0;
      if (pr.includes('cloud') && p.cloud) s += 10;
      return s;
    }
    const rankOf = (items, prefer = [], needWeather = false) => {
      const uniq = items.filter(Boolean).filter((x, i, a) => a.findIndex(y => y.id === x.id) === i);
      const sc = x => featureScore(x) + (prefer.includes(x.id) ? 3 : 0) + (needWeather && weatherproof(x) ? 40 : 0) + (onPreferredApp(x) ? PREFERRED_APP_BOOST.item : 0);
      return uniq.sort((a, b) => sc(b) - sc(a) || a.price - b.price);
    };
    const bestOf = (items, prefer = [], needWeather = false) => rankOf(items, prefer, needWeather)[0] || null;
    /* How many choices to show for indoor cameras on the results step */
    const INDOOR_OPTIONS = 2;

    /* ----- Feature coverage, judged per area ----- */
    const FEATS = {
      res: { w: 10, v: it => (it.res === '4K' || it.res === '2K' ? 1 : 0), yes: '4K or 2K+ resolution', no: 'Below 2K resolution' },
      lens: { w: 10, v: it => (hasLens(it) ? 1 : 0), yes: 'Pan-tilt or dual lens', no: 'No pan-tilt or dual-lens cameras' },
      night: { w: 10, v: it => (it.color ? 1 : 0), yes: 'Color night vision', no: 'Black-and-white night vision only' },
      deter: { w: 14, v: it => (it.deter ? 1 : 0), yes: 'Active deterrence', no: 'No active deterrence' },
      detect: { w: 12, v: it => { const d = it.detect || []; if (!state.detections.length) return d.length ? 1 : 0; return state.detections.filter(x => d.includes(x)).length / state.detections.length; } },
      audio: { w: 10, v: it => (it.audio === '2way' ? 1 : it.audio === 'listen' ? 0.4 : 0), yes: 'Two-way talk', no: 'No camera audio' },
      cloud: { w: 12, v: it => (it.cloud ? 1 : 0), yes: 'Optional cloud backup', no: 'No cloud backup option' },
      continuous: { w: 30, v: (it, l) => (l.rec || sd247(it) ? 1 : 0), yes: 'Records 24/7', no: 'Records motion clips only. For 24/7 recording, choose plug-in Wi-Fi cameras with a microSD card, or a wired security system' }
    };
    const zoneItems = st => st.lines.filter(l => l.on && l.zones && l.zones.length).flatMap(l => l.zones.map(z => ({ z, l })));
    function share(st, k) {
      const zi = zoneItems(st);
      return zi.length ? zi.reduce((s, { l }) => s + FEATS[k].v(l.item, l), 0) / zi.length : 0;
    }
    const totalOf = st => [...st.lines, ...st.extras].filter(l => l.on).reduce((t, l) => t + l.item.price * l.qty, 0);

    function approach(st) {
      const t = st.system ? st.system.type : null;
      const wifiParts = st.lines.some(l => !['system', 'addcam', 'nvr', 'ipcam'].includes(l.role) && !isWired(l.item));
      if (st.nvr) return wifiParts ? `Custom ${st.nvr.series ? st.nvr.series + ' Series ' : ''}IP system with Wi-Fi cameras` : `Custom ${st.nvr.series ? st.nvr.series + ' Series ' : ''}IP system`;
      if (t === 'ip') return wifiParts ? 'IP system with Wi-Fi cameras' : 'IP security system';
      if (t === 'analog') return wifiParts ? 'Analog system with Wi-Fi cameras' : 'Analog security system';
      const allBattery = st.lines.every(l => isBattery(l.item));
      const base = allBattery ? 'All wire-free' : 'All Wi-Fi';
      return st.lines.some(l => l.role === 'flood') && st.lines.some(l => l.role === 'pack') ? `${base} with floodlights` : base;
    }

    /* Cheapest mix of one product's pack sizes (1, 2, 4 cameras...) that covers n areas */
    function packCombo(variants, n) {
      const maxC = Math.max(...variants.map(v => v.cams)), lim = n + maxC;
      const dp = Array(lim + 1).fill(Infinity), from = Array(lim + 1).fill(null);
      dp[0] = 0;
      for (let k = 1; k <= lim; k++) for (const v of variants) {
        if (v.cams <= k && dp[k - v.cams] + v.price < dp[k]) { dp[k] = dp[k - v.cams] + v.price; from[k] = v; }
      }
      let bestK = -1;
      for (let k = n; k <= lim; k++) if (dp[k] < Infinity && (bestK < 0 || dp[k] < dp[bestK])) bestK = k;
      if (bestK < 0) return null;
      const counts = new Map();
      for (let k = bestK; k > 0; k -= from[k].cams) counts.set(from[k], (counts.get(from[k]) || 0) + 1);
      return [...counts].sort((a, b) => b[0].cams - a[0].cams).map(([item, qty]) => ({ item, qty }));
    }

    /* ----- IP pairing rules -----
       IP cameras, PoE floodlights and PoE doorbells only work with a recorder, and only one
       from the same jasper.family-series (e.g. "IP Security Camera V Series" + "Security NVR V Series").
       Complementary products picked by merchandising are trusted when a series is missing. */
    const pairs = (it, R, fromComp) => {
      if (!R) return false;
      if (it.series && R.series) return it.series === R.series;
      return fromComp;
    };
    /* Can this product be used in a setup with recorder R (or none)? */
    const usable = (it, R, path, compIds) => {
      if (!isWired(it)) return true;
      if (!R || path !== 'ip') return false;
      return pairs(it, R, compIds.includes(it.id));
    };

    /* ----- App matching (jasper.remote-connectivity) -----
       Every product in a setup should work in the same app. Candidates on the setup's app win;
       products with no app listed come next; anything else is only used when nothing matches,
       and the results step then says the apps differ. */
    function sameApp(cands, app) {
      if (!app.length) return cands;
      const exact = cands.filter(x => (x.apps || []).some(a => app.includes(a)));
      if (exact.length) return exact;
      const blank = cands.filter(x => !(x.apps || []).length);
      return blank.length ? blank : cands;
    }
    /* Lines that have an app (cables and hard drives don't) */
    const appLines = st => [...st.lines, ...st.extras].filter(l => l.on && (l.item.apps || []).length);
    /* True when no single app works with every selected product */
    const appsDiffer = st => {
      const ls = appLines(st);
      if (ls.length < 2) return false;
      return !ls[0].item.apps.some(a => ls.every(l => l.item.apps.includes(a)));
    };

    /* Wi-Fi pack lines covering zones, using the cheapest mix of the product's pack sizes */
    function packLinesFor(group, zones) {
      const combo = packCombo(group, zones.length);
      if (!combo) return null;
      let i = 0;
      const lines = combo.map(({ item, qty }) => {
        const zs = zones.slice(i, i + item.cams * qty); i += zs.length;
        return { key: `pack-${item.id}`, role: 'pack', item, qty, core: true, on: true, zones: zs, rec: false, adjustable: true };
      });
      const spare = combo.reduce((t, c) => t + c.item.cams * c.qty, 0) - zones.length;
      lines[lines.length - 1].spare = spare;
      return { lines, spare };
    }

    /* ----- Build one candidate setup -----
       path: 'wifi' | 'ip' | 'analog'. S: a kit with a recorder. pack: wire-free pack sizes.
       N: a standalone NVR, filled with same-series IP cameras.
       mix: Wi-Fi pack sizes for a wired + Wi-Fi setup. Areas marked far (and any beyond the
       kit's cameras) go to Wi-Fi cameras; the rest go to the wired system.
       Z.batteryOnly limits every product to battery cameras (wire-free install). */
    function make(path, S, pack, floodMode, Z, N, mix) {
      const { D, I, O } = Z;
      const ok = it => !Z.batteryOnly || runsOnBattery(it);
      /* A wired install choice always gets a recorder for its outdoor areas */
      if (path !== 'wifi' && !S && !N && O.length) return null;
      const R = S || N || null;
      const recKey = S ? 'system' : N ? 'nvr' : null;
      const ad = S ? S.addons : N ? N.addons : {};
      const compIds = Object.values(ad).filter(Boolean).map(x => x.id);
      const toMain = indoorToMain(path) && (S || pack || N);
      let main = [...O, ...(toMain ? I : [])];
      const lines = [], uncovered = [];
      let spare = 0;
      const recordsOn = it => !!R && path === 'ip' && isWired(it);
      /* The setup's app comes from the recorder or Wi-Fi pack, else from the first product picked */
      let app = [...((R || (pack && pack[0]) || {}).apps || [])];
      const onApp = cands => sameApp(cands, app);
      const useApp = it => {
        const a = (it && it.apps) || [];
        if (!a.length) return;
        if (!app.length) { app = [...a]; return; }
        const both = app.filter(x => a.includes(x));
        if (both.length) app = both;
      };

      if (floodMode) {
        /* Floodlights only ever cover outdoor areas marked flood (driveway, backyard) */
        const fz = main.filter(z => z.flood && z.weather);
        const cands = onApp([...ACC.floodlight, ad.floodlight].filter(Boolean).filter(f => usable(f, R, path, compIds) && ok(f)));
        const fl = bestOf(cands, ad.floodlight ? [ad.floodlight.id] : [], fz.some(z => z.weather));
        if (!fz.length || !fl) return null;
        useApp(fl);
        main = main.filter(z => !z.flood);
        const rec = recordsOn(fl);
        lines.push({ key: 'flood', role: 'flood', item: fl, qty: fz.length, zones: fz, on: true, rec, needs: rec ? recKey : null, adjustable: !isWired(fl) });
      }
      const wiredFloods = () => lines.filter(l => l.role === 'flood' && l.rec).reduce((t, l) => t + l.qty, 0);

      /* Areas marked wireless never go on a wired recorder; a wired setup covers them with
         Wi-Fi cameras (mix), or isn't built at all */
      const wiredRec = (S && S.type !== 'wifi') || !!N;
      const wl = wiredRec ? main.filter(z => z.wireless) : [];
      if (wl.length && !mix) return null;
      main = main.filter(z => !wl.includes(z));

      /* Wired + Wi-Fi mix: the wired system takes the easy-to-cable areas first */
      let mixZones = [];
      if (mix) {
        if (!S && !N) return null;
        if (Z.hybrid) {
          const near = main.filter(z => !z.far), far = main.filter(z => z.far);
          if (S) {
            const order = [...near, ...far];
            let wired = order.slice(0, S.cams);
            mixZones = order.slice(S.cams);
            if (!mixZones.length) { mixZones = wired.filter(z => z.far); wired = wired.filter(z => !z.far); }
            main = wired;
          } else {
            mixZones = far; main = near;
          }
        }
        /* Outside a wired + Wi-Fi install, Wi-Fi cameras only cover the wireless-only areas */
        mixZones = [...wl, ...mixZones];
        if (!mixZones.length || !main.length) return null;
      }

      if (S) {
        if (!main.length) return null;
        const n = main.length, c = Math.min(S.cams, n), rec = S.type === 'ip' || S.type === 'analog';
        spare = Math.max(S.cams - n, 0);
        lines.unshift({ key: 'system', role: 'system', item: S, qty: 1, core: true, on: true, zones: main.slice(0, c), rec, spare });
        const short = n - c;
        if (short) {
          const cap = Math.max((S.ch || S.cams) - S.cams - wiredFloods(), 0);
          const addZones = main.slice(c);
          const cands = onApp(S.type === 'ip'
            ? [ad.camera, ...ACC.ipcam].filter(Boolean).filter(x => pairs(x, S, compIds.includes(x.id)))
            : [ad.camera].filter(Boolean));
          const cam = bestOf(cands, ad.camera ? [ad.camera.id] : [], addZones.some(z => z.weather));
          const add = cam ? Math.min(short, cap) : 0;
          if (add) lines.push({ key: 'addcam', role: 'addcam', item: cam, qty: add, zones: addZones.slice(0, add), on: true, rec, needs: 'system' });
          uncovered.push(...addZones.slice(add));
        }
      } else if (N) {
        if (!main.length) return null;
        const cands = onApp(ACC.ipcam.filter(x => pairs(x, N, false)));
        const cam = bestOf(cands, [], main.some(z => z.weather));
        if (!cam) return null;
        useApp(cam);
        const cap = Math.max(N.ch - wiredFloods(), 0);
        const add = Math.min(main.length, cap);
        if (!add) return null;
        lines.unshift({ key: 'nvr', role: 'nvr', item: N, qty: 1, core: true, on: true, zones: [], rec: false, camCount: add });
        lines.splice(1, 0, { key: 'ipcam', role: 'ipcam', item: cam, qty: add, zones: main.slice(0, add), on: true, rec: true, needs: 'nvr' });
        uncovered.push(...main.slice(add));
      } else if (pack) {
        if (!main.length) return null;
        const p = packLinesFor(pack, main);
        if (!p) return null;
        spare = p.spare;
        lines.unshift(...p.lines);
      } else if (main.length) {
        return null;
      }
      if (mix) {
        const p = packLinesFor(mix, mixZones);
        if (!p) return null;
        spare += p.spare;
        /* List the Wi-Fi cameras right after the wired cameras */
        const at = lines.map(l => ['system', 'addcam', 'nvr', 'ipcam'].includes(l.role)).lastIndexOf(true) + 1;
        lines.splice(at, 0, ...p.lines);
      }
      if (!toMain && I.length) {
        const ranked = rankOf(onApp([...ACC.indoor, ad.indoor].filter(Boolean).filter(ok)), ad.indoor ? [ad.indoor.id] : []);
        const ic = ranked[0];
        useApp(ic);
        if (ic) lines.push({ key: 'indoor', role: 'indoor', item: ic, qty: I.length, zones: I, on: true, adjustable: true, options: ranked.length > 1 ? ranked.slice(0, INDOOR_OPTIONS) : null });
        else uncovered.push(...I);
      }
      /* Doorbells only ever cover the front door */
      if (D.length && D.every(z => z.kind === 'doorbell')) {
        const cands = onApp([...ACC.doorbell, ad.doorbell].filter(Boolean).filter(d => usable(d, R, path, compIds) && ok(d)));
        const db = bestOf(cands, ad.doorbell ? [ad.doorbell.id] : []);
        if (db) {
          useApp(db);
          const rec = recordsOn(db);
          lines.push({ key: 'doorbell', role: 'doorbell', item: db, qty: D.length, zones: D, on: true, rec, needs: rec ? recKey : null });
        } else uncovered.push(...D);
      }
      if (!lines.length) return null;

      /* Add-ons must work in the setup's app: the app every listed product shares, else the
         setup's app. Products with no app listed (cables, hard drives) always fit. */
      const withApps = lines.filter(l => (l.item.apps || []).length);
      const shared = withApps.length ? withApps[0].item.apps.filter(a => withApps.every(l => l.item.apps.includes(a))) : [];
      const setupApps = shared.length ? shared : app;
      const appFits = it => !(it.apps || []).length || !setupApps.length || it.apps.some(a => setupApps.includes(a));

      const extras = [];
      if (!floodMode) {
        const fz = O.find(z => z.flood && z.weather);
        const fl = fz && bestOf([...ACC.floodlight, ad.floodlight].filter(Boolean).filter(f => usable(f, R, path, compIds) && ok(f) && appFits(f)), [], true);
        if (fl) {
          const rec = recordsOn(fl);
          extras.push({ key: 'floodx', role: 'flood', item: fl, qty: 1, on: false, rec, needs: rec ? recKey : null, adjustable: !isWired(fl), why: `Adds bright light and video on motion for your ${fz.label.toLowerCase()}` });
        }
      }
      /* Component-only cameras ship without cables: add the matching cable, one per camera */
      const needCables = lines.filter(l => l.item.componentOnly && isWired(l.item));
      const camCount = needCables.length ? lines.filter(l => l.rec).reduce((t, l) => t + (l.role === 'system' ? l.item.cams : l.qty), 0) : 0;
      const cable = camCount && pickCable(path === 'analog' ? 'analog' : 'ip');
      const reuse = path === 'analog' && state.install === 'coax';
      if (cable) extras.push({ key: 'cables', role: 'cable', item: cable, qty: camCount, on: !reuse, why: `Camera cables aren\u2019t included with ${joinAnd([...new Set(needCables.map(l => l.item.name))])}. One per camera connected to the ${path === 'analog' ? 'DVR' : 'NVR'}${reuse ? '. Skip these if your existing cables reach each camera' : ''}`, adjustable: true });
      if (R && R.ch && ad.drive && appFits(ad.drive)) extras.push({ key: 'drive', role: 'drive', item: ad.drive, qty: 1, on: state.priorities.includes('continuous'), needs: recKey, why: 'Keeps a longer history of recorded video' });

      /* Complementary products (Search & Discovery) for anything in the setup, unchecked.
         Skips kits and recorders, products already listed, and wired cameras that can't
         connect to this setup's recorder. */
      const listed = new Set([...lines, ...extras].flatMap(l => [l.item.id, String(l.item.product_id)]));
      let nComps = 0;
      for (const l of lines) {
        for (const c of l.item.comps || []) {
          if (nComps >= MAX_COMPS) break;
          if (listed.has(c.id) || listed.has(String(c.product_id))) continue;
          if (!appFits(c)) continue;
          if (['ip', 'analog', 'wifi', 'nvr'].includes(c.role)) continue;
          if (c.role === 'ipcam' && !(R && path === 'ip' && pairs(c, R, true))) continue;
          if (['doorbell', 'floodlight', 'indoor'].includes(c.role) && (!usable(c, R, path, [c.id]) || !ok(c))) continue;
          listed.add(c.id); listed.add(String(c.product_id));
          const rec = recordsOn(c);
          extras.push({ key: `comp-${c.id}`, role: 'comp', item: c, qty: 1, on: false, rec, needs: rec ? recKey : null, adjustable: true, why: `Pairs well with the ${l.item.name}` });
          nComps++;
        }
      }

      const st = { path, system: S, nvr: N || null, recorder: R, recKey, pack: pack ? (lines.find(l => l.role === 'pack') || {}).item : null, floodMode, lines, extras, uncovered, spare, outdoorCount: O.length };
      st.id = lines.map(l => `${l.item.id}x${l.qty}`).join('.');
      st.total = totalOf(st);
      return st;
    }

    const componentOnlyLines = st => st.lines.filter(l => l.on && l.item.componentOnly && isWired(l.item));
    /* Cables come from the cables collection, filtered by tag */
    const CABLE_TAG = { ip: /ip camera cables?/i, analog: /analog camera cables?/i };
    function pickCable(kind) {
      const c = (DATA.cables || []).filter(x => x.available && (x.tags || []).some(t => CABLE_TAG[kind].test(t)));
      const best = c.length ? c.reduce((a, b) => (b.price < a.price ? b : a)) : null;
      return best ? { ...best, id: String(best.variant_id), role: 'cable' } : null;
    }

    /* Camera quality for "Best performance": features every camera can have, plus resolution */
    const RES_Q = { '4K': 1.5, '2K': 0.75 };
    const itemQuality = it => (it.color ? 1 : 0) + (it.deter ? 1 : 0) + Math.min((it.detect || []).length, 3) / 3
      + (it.audio === '2way' ? 1 : it.audio === 'listen' ? 0.4 : 0) + (it.cloud ? 0.3 : 0)
      + (RES_Q[it.res] || 0) + 0.5 * (it.camFeatures || []).length;
    /* Average quality across the picked areas */
    function quality(st) {
      const zi = zoneItems(st);
      return zi.length ? zi.reduce((t, { l }) => t + itemQuality(l.item), 0) / zi.length : 0;
    }
    const prefOf = () => (BUDGET[state.budget] ? state.budget : 'skip');

    function scoreSetup(st, pref = prefOf()) {
      let s = -st.uncovered.length * 40 - st.spare * 3 - (st.lines.length - 1) * 2;
      s -= zoneItems(st).filter(({ z, l }) => z.weather && !weatherproof(l.item)).length * 30;
      /* Products that need different apps rank below a setup that works in one app */
      if (appsDiffer(st)) s -= 35;
      /* Setups on the preferred app (Lorex Connect) rank higher */
      const al = appLines(st);
      if (al.length) s += PREFERRED_APP_BOOST.setup * al.filter(l => onPreferredApp(l.item)).length / al.length;
      state.priorities.forEach(k => { s += FEATS[k].w * share(st, k); });
      s -= (st.total / SCALE) * PRICE_WEIGHT[pref];
      if (pref === 'premium') s += quality(st) * QUALITY_WEIGHT;
      if (state.install === 'unsure' && !(state.property && PROPS[state.property].wireFree)) {
        const wired = !!st.recorder && st.path !== 'wifi', n = st.outdoorCount;
        s += wired ? (n >= 4 ? 6 : -3) : (n <= 3 ? 6 : -3);
      }
      /* Wired + Wi-Fi install: favour setups that really mix wired and Wi-Fi cameras */
      if (state.install === 'hybrid' && isMix(st)) s += 12;
      return s;
    }
    const isMix = st => !!st.recorder && st.lines.some(l => l.role === 'pack');

    /* How many Wi-Fi products to try against each recorder in a wired + Wi-Fi mix */
    const MIX_PACKS = 4;

    function generate(types, Z) {
      const out = [], push = x => { if (x) out.push(x); };
      const ok = it => !Z.batteryOnly || runsOnBattery(it);
      const systems = P.filter(p => types.includes(p.type) && !(p.type === 'wifi' && !p.ch) && ok(p));
      const packGroups = {};
      P.filter(p => p.type === 'wifi' && !p.ch && ok(p)).forEach(p => { (packGroups[p.product_id] = packGroups[p.product_id] || []).push(p); });
      const groups = Object.values(packGroups);
      for (const S of systems) { push(make(S.type, S, null, false, Z)); push(make(S.type, S, null, true, Z)); }
      if (types.includes('wifi')) for (const g of groups) { push(make('wifi', null, g, false, Z)); push(make('wifi', null, g, true, Z)); }
      if (types.includes('ip')) for (const N of ACC.nvr) { push(make('ip', null, null, false, Z, N)); push(make('ip', null, null, true, Z, N)); }
      for (const path of types) { push(make(path, null, null, true, Z)); push(make(path, null, null, false, Z)); }
      /* Mixes: for a wired + Wi-Fi install, or when a picked area must be wireless (e.g. the shed) */
      if (Z.hybrid || Z.O.some(z => z.wireless)) {
        const top = rankOf(groups.map(g => g[0]), [], true).slice(0, MIX_PACKS).map(it => groups.find(g => g[0] === it));
        for (const g of top) {
          for (const S of systems.filter(x => x.type !== 'wifi')) { push(make(S.type, S, null, false, Z, null, g)); push(make(S.type, S, null, true, Z, null, g)); }
          if (types.includes('ip')) for (const N of ACC.nvr) { push(make('ip', null, null, false, Z, N, g)); push(make('ip', null, null, true, Z, N, g)); }
        }
      }
      return out;
    }

    function buildSetups() {
      const Z = { ...splitZones(), batteryOnly: state.install === 'wirefree', hybrid: state.install === 'hybrid' };
      let fallback = false;
      let list = generate(allowedTypes(), Z);
      if (!list.some(s => !s.uncovered.length)) {
        const wider = generate(['wifi', 'ip', 'analog'], { ...Z, batteryOnly: false });
        if (wider.some(s => !s.uncovered.length)) { list = wider; fallback = true; }
      }
      const seen = new Set();
      list = list.filter(s => (seen.has(s.id) ? false : seen.add(s.id)));
      list.forEach(s => { s.fallback = fallback; s.score = scoreSetup(s); });
      return list.sort((a, b) => b.score - a.score);
    }

    const wxIssues = st => zoneItems(st).filter(({ z, l }) => z.weather && !weatherproof(l.item)).length;

    /* Swap a line to another variant of the same product (color, pack size...).
       A different camera count on the system re-balances areas with the add-on cameras. */
    function applyVariant(st, l, v) {
      const prev = l.item;
      l.item = { ...prev, id: v.id, variant_id: v.variant_id, sku: v.sku, price: v.price, url: v.url, image: v.image || prev.image, name: v.name };
      if (prev.cams !== undefined) l.item.cams = v.cams;
      if (st.system === prev) st.system = l.item;
      if (st.recorder === prev) st.recorder = l.item;
      if (st.pack === prev) st.pack = l.item;
      if (l.role !== 'system' || v.cams === prev.cams) return;
      if (v.cams < l.zones.length) {
        st.uncovered = [...st.uncovered, ...l.zones.slice(v.cams)];
        l.zones = l.zones.slice(0, v.cams);
      }
      let room = v.cams - l.zones.length;
      const add = st.lines.find(x => x.role === 'addcam');
      if (add && room > 0) {
        const n = Math.min(room, add.zones.length);
        l.zones = [...l.zones, ...add.zones.slice(0, n)];
        add.zones = add.zones.slice(n); add.qty -= n; room -= n;
        if (!add.qty) st.lines = st.lines.filter(x => x !== add);
      }
      l.spare = Math.max(room, 0);
    }

    function recommend() {
      const list = buildSetups();
      if (!list.length) return null;
      const best = list[0];
      const chosen = (state.pick && list.find(s => s.id === state.pick)) || best;
      [...chosen.lines, ...chosen.extras].forEach(l => { if (state.sel[l.key] !== undefined) l.on = state.sel[l.key]; });
      /* IP products never go to the cart without their recorder */
      const recLine = chosen.lines.find(l => l.key === chosen.recKey);
      if (recLine && !recLine.on) [...chosen.lines, ...chosen.extras].forEach(l => { if (l.needs === chosen.recKey) l.on = false; });
      [...chosen.lines, ...chosen.extras].forEach(l => {
        if ((l.adjustable || chosen.extras.includes(l)) && state.qty[l.key]) l.qty = state.qty[l.key];
        if (l.options && state.choice[l.key]) { const o = l.options.find(x => x.id === state.choice[l.key]); if (o) l.item = o; }
        const v = state.variant[l.key] && (l.item.variants || []).find(x => x.id === state.variant[l.key]);
        if (v && v.id !== l.item.id) applyVariant(chosen, l, v);
      });
      chosen.total = totalOf(chosen);

      /* Other options: the budget-friendly and/or best-performance counterpart to the shopper's
         choice. Each must cover as much as the current setup, with no new weather or app issues,
         and actually be cheaper (budget) or better equipped (performance). */
      const mixWorse = s => appsDiffer(s) && !appsDiffer(chosen);
      const fair = list.filter(s => s !== chosen && s.uncovered.length <= chosen.uncovered.length && wxIssues(s) <= wxIssues(chosen) && !mixWorse(s));
      const topFor = (cands, pref) => cands.map(s => ({ s, v: scoreSetup(s, pref) })).sort((a, b) => b.v - a.v).map(x => x.s)[0] || null;
      const pref = prefOf();
      const alts = [];
      if (chosen !== best) alts.push({ kind: 'best', s: best });
      const wants = { budget: ['premium'], premium: ['budget'], skip: ['budget', 'premium'] }[pref];
      for (const k of wants) {
        const q = quality(chosen);
        const s = topFor(fair.filter(x => (k === 'budget' ? x.total < chosen.total : quality(x) > q + 0.05)), k);
        if (s && !alts.some(a => a.s === s)) alts.push({ kind: k, s });
      }
      const fullCover = list.filter(s => !s.uncovered.length && !wxIssues(s));
      const lowest = fullCover.length ? Math.min(...fullCover.map(s => s.total)) : null;
      return { ...chosen, best, alts, isPick: chosen !== best, lowest };
    }

    /* ----- Explain a setup ----- */
    function detectLine(st) {
      const items = st.lines.filter(l => l.on && l.zones && l.zones.length);
      const want = state.detections, names = a => joinAnd(a.map(d => DETECTIONS[d].toLowerCase()));
      if (!want.length) {
        const sh = share(st, 'detect');
        if (!sh) return ['miss', 'Motion alerts only, no smart detection'];
        const roles = [...new Set(items.filter(l => (l.item.detect || []).length).map(l => ROLE_NAME[l.role]))];
        return sh >= 0.999 ? ['ok', 'Smart alerts on every camera'] : ['info', `Smart alerts on the ${joinAnd(roles)}`];
      }
      const have = want.filter(d => items.some(l => (l.item.detect || []).includes(d)));
      const missing = want.filter(d => !have.includes(d));
      if (!have.length) return ['miss', `Doesn\u2019t recognize ${names(want)}`];
      if (missing.length) return ['info', `Recognizes ${names(have)}, not ${names(missing)}`];
      return share(st, 'detect') >= 0.999 ? ['ok', `Recognizes ${names(have)} on every camera`] : ['ok', `Recognizes ${names(have)}`];
    }
    /* Deterrence reads the product's own values from facet.active_deterrence */
    function deterLine(st) {
      const items = st.lines.filter(l => l.on && l.zones && l.zones.length);
      const withIt = items.filter(l => l.item.deter);
      if (!withIt.length) return ['miss', 'No active deterrence'];
      const vals = [...new Set(withIt.flatMap(l => l.item.deterList || []).map(v => v.toLowerCase()))];
      const detail = vals.length ? ` (${joinAnd(vals)})` : '';
      if (share(st, 'deter') >= 0.999) return ['ok', `Active deterrence${zoneItems(st).length > 1 ? ' on every camera' : ''}${detail}`];
      return ['info', `Active deterrence on the ${joinAnd([...new Set(withIt.map(l => ROLE_NAME[l.role]))])}${detail}`];
    }
    function featureLine(st, k) {
      if (k === 'detect') return detectLine(st);
      if (k === 'deter') return deterLine(st);
      const F = FEATS[k], sh = share(st, k), items = st.lines.filter(l => l.on && l.zones && l.zones.length);
      const multi = zoneItems(st).length > 1;
      if (k === 'continuous' && sh >= 0.999) {
        /* Say where the 24/7 video goes: the recorder, microSD cards, or both */
        const zi = zoneItems(st), toRec = zi.filter(({ l }) => l.rec).length;
        return ['ok', toRec === zi.length ? 'Records 24/7 to the recorder'
          : !toRec ? `Records 24/7 to a microSD card in ${multi ? 'each camera' : 'the camera'}`
          : 'Records 24/7: wired cameras to the recorder, Wi-Fi cameras to a microSD card'];
      }
      if (k === 'res' && sh >= 0.999 && zoneItems(st).every(({ l }) => l.item.res === '4K')) return ['ok', `4K resolution${multi ? ' on every camera' : ''}`];
      if (sh >= 0.999) return ['ok', `${F.yes}${multi ? ' on every camera' : ''}`];
      if (k === 'audio') {
        const two = items.filter(l => l.item.audio === '2way').map(l => ROLE_NAME[l.role]);
        const listen = items.filter(l => l.item.audio === 'listen').map(l => ROLE_NAME[l.role]);
        if (two.length) return ['info', `Two-way talk on the ${joinAnd([...new Set(two)])}${listen.length ? `, listen-in on the ${joinAnd([...new Set(listen)])}` : ''}`];
        if (listen.length) return ['info', 'Listen-in audio, no talk-back'];
        return ['miss', F.no];
      }
      if (sh > 0) {
        const roles = [...new Set(items.filter(l => F.v(l.item, l) > 0).map(l => ROLE_NAME[l.role]))];
        return ['info', k === 'continuous' ? `Records 24/7 on the ${joinAnd(roles)}` : `${F.yes} on the ${joinAnd(roles)}`];
      }
      if (k === 'continuous' && items.some(l => isBattery(l.item))) return ['miss', 'Battery cameras record motion clips only, not 24/7. For 24/7 recording, choose plug-in Wi-Fi cameras with a microSD card, or a wired security system'];
      return ['miss', F.no];
    }
    /* "Why this fits your answers": one line per answer the shopper gave (areas, install,
       each priority, budget), then facts about the setup itself.
       Each entry is [status, text, answer label]; status is 'ok', 'info' or 'miss'. */
    const low = t => (/^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t);
    function whyList(st) {
      const answers = [], facts = [], S = st.system, R = st.recorder;

      /* Areas */
      const picked = state.zones.map(zoneById).filter(Boolean);
      const areaKey = joinAnd(picked.map(z => z.label + (zoneCount(z.id) > 1 ? ` (${zoneCount(z.id)})` : '')));
      const cams = zoneItems(st).length, n = picked.length;
      answers.push(st.uncovered.length
        ? ['info', `${cap(joinAnd(zoneLabels(st.uncovered)))} ${st.uncovered.length > 1 ? 'aren’t' : 'isn’t'} covered yet`, areaKey]
        : ['ok', `${plural(cams, 'camera')} ${cams === 1 ? 'covers' : 'cover'} ${n === 1 ? 'it' : n === 2 ? 'both areas' : `all ${n} areas`}`, areaKey]);

      /* Install */
      const mixZ = st.lines.filter(l => l.role === 'pack').flatMap(l => l.zones);
      const wifiFill = mixZ.length
        ? `Wi-Fi cameras cover the ${joinAnd(zoneLabels(mixZ))}, where network cables are hard to run`
        : 'Wi-Fi cameras fill in where network cables are hard to run';
      let how;
      if (st.nvr) how = 'IP cameras use one Ethernet cable each for power and video';
      else if (S && S.type !== 'wifi') how = S.type === 'ip' ? 'wired PoE cameras use one Ethernet cable each for power and video' : state.install === 'coax' ? 'reuses the coaxial cables you already have' : 'wired cameras connect to the DVR with one coaxial cable each';
      else how = st.lines.filter(l => !isWired(l.item)).every(l => isBattery(l.item)) ? 'battery powered and connects to Wi-Fi, so there are no cables to run' : 'connects over Wi-Fi, so there are no network cables to run';
      const installKey = state.install === 'unsure' ? 'Not sure about install' : INSTALL[state.install].short;
      if (st.fallback) answers.push(['info', `nothing available fits this install for every area, so this is the closest match`, installKey]);
      else if (state.install === 'unsure') {
        answers.push(['ok', R && st.path !== 'wifi'
          ? (state.priorities.includes('continuous') ? 'a wired system gives the most reliable 24/7 recording' : 'for 4 or more outdoor cameras, a wired system gives the steadiest picture')
          : st.outdoorCount <= 3 ? 'Wi-Fi cameras suit this size of setup' : 'Wi-Fi cameras cover these areas without running network cables', installKey]);
      } else answers.push(['ok', how, installKey]);
      if ((st.nvr || (S && S.type !== 'wifi')) && approach(st).includes('Wi-Fi')) facts.push(['ok', wifiFill]);

      /* Priorities and budget */
      state.priorities.forEach(k => { const [c, t] = featureLine(st, k); answers.push([c, low(t), PRIOS[k].label]); });
      if (!st.isPick && state.budget === 'budget') {
        answers.push(['ok', st.lowest != null && st.total <= st.lowest ? 'the lowest price that covers every area you picked' : 'chosen to keep the price down while covering your areas', BUDGET.budget.label]);
      } else if (!st.isPick && state.budget === 'premium') {
        answers.push(['ok', 'chosen for the best cameras and features for your areas', BUDGET.premium.label]);
      }

      /* Facts about the setup */
      const outdoor = zoneItems(st).filter(({ z }) => z.weather);
      if (outdoor.length) {
        const bad = [...new Set(outdoor.filter(({ l }) => !weatherproof(l.item)).map(({ l }) => l.item.name))];
        const ratings = [...new Set(outdoor.filter(({ l }) => weatherproof(l.item)).map(({ l }) => l.item.ip))].sort();
        facts.push(bad.length ? ['info', `Check the weather rating before mounting outside: ${joinAnd(bad)}`] : ['ok', `Weatherproof outdoor cameras (${joinAnd(ratings)}) for rain, snow and dust`]);
      }
      const al = appLines(st);
      if (appsDiffer(st)) {
        const byApp = new Map();
        al.forEach(l => {
          const nm = l.item.appNames.join(' or ');
          byApp.set(nm, [...new Set([...(byApp.get(nm) || []), ROLE_NAME[l.role] || l.item.name])]);
        });
        const parts = [...byApp].map(([nm, roles]) => `the ${joinAnd(roles)} ${roles.length > 1 || /s$/.test(roles[0]) ? 'use' : 'uses'} ${nm}`);
        facts.push(['info', `Compatible apps differ: ${joinAnd(parts)}. You may need more than one app to see all your cameras`]);
      } else if (al.length) {
        const k = al[0].item.apps.find(a => al.every(l => l.item.apps.includes(a)));
        const name = al[0].item.appNames.find(nm => appKey(nm) === k) || k;
        facts.push(['ok', `${al.length > 1 ? 'Everything works' : 'Works'} in the ${name} app`.replace(/ app app$/i, ' app')]);
      }
      if (R && R.ch) {
        const used = st.lines.filter(l => l.rec).reduce((t, l) => t + (l.role === 'system' ? l.item.cams : l.qty), 0);
        if (R.ch > used) facts.push(['ok', `Recorder has room for ${plural(R.ch - used, 'more camera')} later`]);
      }
      const paired = st.lines.filter(l => l.needs && l.role !== 'drive' && isWired(l.item));
      if (R && R.series && paired.length) facts.push(['ok', `${st.nvr ? 'NVR and IP cameras are' : 'Add-on IP products are'} all ${R.series} Series, so they work together`]);
      const plugs = [...new Set(st.lines.filter(l => l.on && isPlugIn(l.item)).map(l => ROLE_NAME[l.role]))];
      if (plugs.length) facts.push(['info', `The ${joinAnd(plugs)} ${plugs.length > 1 || /s$/.test(plugs[0]) ? 'need' : 'needs'} a nearby power outlet. Wi-Fi replaces the network cable, not the power cable`]);
      st.lines.filter(l => l.on && isHardwired(l.item) && HARDWIRE_WHERE[l.role])
        .forEach(l => facts.push(['info', `The ${ROLE_NAME[l.role]} connects over Wi-Fi but is powered by ${HARDWIRE_WHERE[l.role]}`]));
      const unc2 = componentOnlyLines(st);
      if (unc2.length) {
        const cable = st.extras.find(l => l.key === 'cables');
        const reuse = st.path === 'analog' && state.install === 'coax';
        facts.push(['info', `Camera cables aren’t included with ${joinAnd([...new Set(unc2.map(l => l.item.name))])}. ${reuse ? 'You can reuse your existing cables, or add new ones below' : cable ? 'Matching cables are in the add-ons below' : 'You’ll need a cable for each camera'}`]);
      }
      const ORDER = { ok: 0, info: 1, miss: 2 };
      return [...answers, ...facts.sort((a, b) => ORDER[a[0]] - ORDER[b[0]])];
    }
    function lineWhy(l) {
      if (l.why) return l.why;
      if (l.role === 'nvr') return `Records your ${plural(l.camCount, 'IP camera')}. ${l.item.ch}-channel, room to add more later${l.item.series ? `. ${l.item.series} Series` : ''}`;
      const where = `For ${joinAnd(zoneLabels(l.zones))}`;
      const it = l.item;
      const tail = {
        system: it.ch ? `. ${plural(it.cams, 'camera')} and ${/^8|^11|^18/.test(String(it.ch)) ? 'an' : 'a'} ${it.ch}-channel recorder` : `. ${plural(it.cams, 'camera')}`,
        addcam: '. Connects to your recorder',
        ipcam: '. Connects to the NVR with one Ethernet cable',
        pack: `. ${cap(powerText(it))}`,
        flood: isWired(it) ? '. Bright light and video on motion, connects to your recorder' : `. Bright light and video on motion, ${powerText(it, 'flood')}`,
        indoor: `. ${cap(powerText(it))}`,
        doorbell: isWired(it) ? '. See and answer visitors from your phone' : `. See and answer visitors from your phone, ${powerText(it, 'doorbell')}`
      }[l.role] || '';
      const wx = l.zones.some(z => z.weather) && weatherproof(it) ? `. Weatherproof (${it.ip})` : '';
      const clip = !state.priorities.includes('continuous') ? ''
        : isBattery(it) ? '. Records motion clips, not 24/7'
        : sd247(it) && !l.rec ? '. Records 24/7 to a microSD card' : '';
      const provided = l.qty * (l.role === 'pack' ? (it.cams || 1) : 1);
      const feat = (it.camFeatures || []).length ? `. ${joinAnd(it.camFeatures)}` : '';
      const short = l.adjustable && l.zones.length > provided ? `. Covers ${provided} of the ${l.zones.length} camera spots at this quantity` : '';
      const noCable = it.componentOnly && isWired(it) ? '. Camera cables not included' : '';
      return `${where}${tail}${l.spare ? `, plus ${plural(l.spare, 'spare camera')}` : ''}${feat}${wx}${clip}${noCable}${short}`;
    }
    function altText(a, c) {
      const bits = [];
      const dq = quality(a) - quality(c);
      if (dq > 0.3) bits.push('better-equipped cameras');
      else if (dq < -0.3) bits.push('fewer camera features');
      const PHRASE = k => PRIOS[k].short;
      state.priorities.forEach(k => {
        const x = share(a, k) - share(c, k);
        if (x > 0.2) bits.push(share(a, k) >= 0.999 ? `${PHRASE(k)} on every camera` : `${PHRASE(k)} on more cameras`);
        else if (x < -0.2) bits.push(share(a, k) === 0 ? `no ${PHRASE(k)}` : `${PHRASE(k)} on fewer cameras`);
      });
      const aw = a.system && a.system.type !== 'wifi', cw = c.system && c.system.type !== 'wifi';
      const af = a.lines.some(l => l.role === 'flood'), cf = c.lines.some(l => l.role === 'flood');
      if (af && !cf) bits.push('adds floodlight cameras for light on motion');
      if (!af && cf) bits.push('no floodlight cameras');
      if (!aw && cw) bits.push('no network cables to run');
      if (aw && !cw) bits.push('wired for the steadiest picture');
      if (appsDiffer(a) && !appsDiffer(c)) bits.push('compatible apps differ between products');
      if (!appsDiffer(a) && appsDiffer(c)) bits.push('everything works in one app');
      return bits.length ? bits.join(', ').replace(/^./, m => m.toUpperCase()) + '.' : 'A different mix of products.';
    }

    /* ----- Plan drawing ----- */
    function conePath(z) {
      const [cx, cy] = z.cam, r = z.len, t = Math.PI / 180;
      const a1 = (z.a - z.sp / 2) * t, a2 = (z.a + z.sp / 2) * t, f = n => n.toFixed(1);
      return `M${cx} ${cy}L${f(cx + r * Math.cos(a1))} ${f(cy + r * Math.sin(a1))}A${r} ${r} 0 0 1 ${f(cx + r * Math.cos(a2))} ${f(cy + r * Math.sin(a2))}Z`;
    }
    function planHTML(scene, sel, interactive, cls = '') {
      const zs = ZONES[scene], photo = IMG[scene];
      const bg = photo ? `<image href="${esc(photo)}" x="0" y="0" width="600" height="420" preserveAspectRatio="xMidYMid slice"/>` : (SCENES[scene] || SCENES.house);
      const cones = zs.map((z, i) => `<path class="cone${sel.includes(z.id) ? ' on' : ''}" data-cone="${z.id}" d="${conePath(z)}" style="transform-origin:${z.cam[0]}px ${z.cam[1]}px;--d:${300 + i * 170}ms;--i:${i}"/>`).join('');
      /* Intro only: a radar-style wave that travels out through each fixed view */
      const pings = cls.includes('is-intro') ? zs.map((z, i) => `<path class="ping" d="${conePath(z)}" style="transform-origin:${z.cam[0]}px ${z.cam[1]}px;--d:${300 + i * 170}ms;--i:${i}"/>`).join('') : '';
      const dots = zs.map((z, i) => `<circle class="cam${sel.includes(z.id) ? ' on' : ''}" data-dot="${z.id}" cx="${z.cam[0]}" cy="${z.cam[1]}" r="5" style="--d:${300 + i * 170}ms"/>`).join('');
      const pins = interactive ? zs.map(z => `<button type="button" class="sf-pin" data-act="zone:${z.id}" aria-pressed="${sel.includes(z.id)}" aria-label="${esc(z.label)}" style="left:${(z.pin[0] / 6).toFixed(2)}%;top:${(z.pin[1] / 4.2).toFixed(2)}%"><i></i><span aria-hidden="true">${esc(z.label)}</span></button>`).join('') : '';
      return `<div class="sf-plan ${photo ? 'has-photo' : ''} ${cls}"><svg viewBox="0 0 600 420" aria-hidden="true">${bg}${cones}${pings}${dots}</svg>${pins}</div>`;
    }
    const thumb = item => item.image ? `<img src="${esc(item.image)}" alt="" loading="lazy" width="56" height="56">` : `<span class="sf-thumb-ph">${icon(item.role === 'doorbell' ? 'talk' : item.role === 'indoor' ? 'house' : item.role === 'nvr' || item.role === 'drive' ? 'drive' : 'record')}</span>`;

    /* ----- Views ----- */
    const stepHead = (n, title, help) => `<header class="sf-qhead"><p class="sf-stepno">Step ${n} of 5</p><h1 data-sf-heading tabindex="-1">${title}</h1>${help ? `<p class="sf-help">${help}</p>` : ''}</header>`;
    const optCard = (act, id, o, pressed, disabled = false) => `<button type="button" class="sf-opt" data-act="${act}:${id}" aria-pressed="${pressed}" ${disabled ? 'disabled' : ''}>${icon(o.icon)}<strong>${o.label}</strong><small>${o.desc}</small></button>`;

    /* Step 1 picture cards: the scene's photo from the theme editor, or the icon */
    const propMedia = (src, ic, check) => `<span class="sf-opt-media">${src ? `<img src="${esc(src)}" alt="" loading="lazy">` : icon(ic)}${check ? '<span class="sf-opt-check" aria-hidden="true"></span>' : ''}</span>`;
    const picCard = (act, id, o, pressed, src) => `<button type="button" class="sf-opt sf-opt--prop" data-act="${act}:${id}" aria-pressed="${pressed}">${propMedia(src, o.icon)}<span class="sf-opt-body"><strong>${o.label}</strong><small>${o.desc}</small></span></button>`;
    /* "Which cables do I have?" pictures: a photo from the theme editor, or the drawing */
    const CABLE_IMG = DATA.cable_images || {};
    const cablePic = (k, svg, alt) => CABLE_IMG[k]
      ? `<img src="${esc(CABLE_IMG[k])}" alt="${alt}" loading="lazy">`
      : `<svg viewBox="0 0 120 80" fill="none" stroke="currentColor" stroke-width="2.4">${svg}</svg>`;
    /* Step 1 is a single-choice radio group: one tab stop (the picked card, else the first), arrows move */
    const propCard = (id, o, i) => {
      const on = state.property === id, tab = state.property ? on : i === 0;
      return `<button type="button" class="sf-opt sf-opt--prop" role="radio" aria-checked="${on}" tabindex="${tab ? 0 : -1}" data-act="prop:${id}">${propMedia(IMG[o.scene || 'house'], o.icon, true)}<span class="sf-opt-body"><strong>${o.label}</strong><small>${o.desc}</small></span></button>`;
    };

    function zoneSummary() {
      const n = state.zones.length;
      if (!n) return '<strong>No areas picked yet</strong>Pick at least one to continue.';
      const zs = state.zones.map(zoneById).filter(Boolean);
      const labels = zs.map(z => esc(z.label) + (zoneCount(z.id) > 1 ? ` (${zoneCount(z.id)})` : ''));
      const cams = zs.reduce((t, z) => t + zoneCount(z.id), 0);
      return `<strong>${plural(n, 'area')} picked${cams > n ? `, ${cams} cameras` : ''}</strong>You picked ${joinAnd(labels)}.`;
    }

    /* Area list: a checkbox per area, plus a camera-count stepper once the area is picked */
    function zoneListHTML() {
      return zonesOf().map(z => {
        const on = state.zones.includes(z.id), n = zoneCount(z.id);
        const stepper = on ?`<span class="sf-qty sf-zqty" role="group" aria-label="Cameras for ${esc(z.label)}">
            <button type="button" data-act="zqty:${z.id}:-1" aria-label="Fewer cameras for ${esc(z.label)}" ${n <= 1 ? 'disabled' : ''}>−</button>
            <output aria-live="polite">${n}</output>
            <button type="button" data-act="zqty:${z.id}:1" aria-label="More cameras for ${esc(z.label)}" ${n >= ZONE_MAX ? 'disabled' : ''}>+</button></span>` : '';
        return `<div class="sf-zitem${on ? ' is-on' : ''}"><label class="sf-zrow"><input type="checkbox" data-act="zone:${z.id}" ${on ? 'checked' : ''}>${esc(z.label)}</label>${stepper}</div>`;
      }).join('');
    }
    /* Redraw the area list and summary in place (keeps the map and its animations) */
    function refreshZones(focusAct) {
      const zl = $main.querySelector('[data-sf-zlist]'); if (zl) zl.innerHTML = zoneListHTML();
      const zc = $main.querySelector('[data-sf-zcount]'); if (zc) zc.innerHTML = zoneSummary();
      if (focusAct) { const el = $main.querySelector(`[data-act="${focusAct}"]`); if (el && !el.disabled) el.focus({ preventScroll: true }); }
      renderBar(); writeHash();
    }

    /* Short feature list used to compare product options */
    function optionFeats(o) {
      const f = [...(o.camFeatures || [])];
      if (o.power) f.push({ battery: 'Battery', plug: 'Plug-in', hardwired: 'Hardwired', flex: 'Battery or hardwired' }[o.power]);
      if (o.audio === '2way') f.push('Two-way talk'); else if (o.audio === 'listen') f.push('Listen-in');
      if (o.color) f.push('Color night vision');
      if ((o.detect || []).length) f.push(`Detects ${joinAnd(o.detect.filter(d => DETECTIONS[d]).map(d => DETECTIONS[d].toLowerCase()))}`);
      if (o.cloud) f.push('Cloud backup');
      if (o.deter) f.push('Active deterrence');
      return f.slice(0, 6);
    }

    /* ----- Variant picker: "Also available in white and 8 cameras" + Change option ----- */
    const isCamOpt = n => /camera/i.test(n);
    const optVal = (n, x) => {
      const s = String(x ?? '').trim();
      if (isCamOpt(n) && /^\d+$/.test(s)) return `${s} ${s === '1' ? 'camera' : 'cameras'}`;
      return /colou?r|finish/i.test(n) ? s.toLowerCase() : s;
    };
    const variantLabel = (it, v) => {
      const names = it.optNames || [];
      const txt = v.opts.length ? v.opts.map((x, i) => optVal(names[i] || '', x)).join(', ') : v.title;
      return `${cap(txt || v.title || '')} – ${fmt(v.price)}`;
    };
    /* Option picker under a product: "Color and number of cameras" + dropdown */
    function variantRow(l) {
      const it = l.item, vs = it.variants || [];
      if (vs.length < 2) return '';
      const names = (it.optNames || []).filter(Boolean);
      const label = names.length ? cap(joinAnd(names.map(n => n.toLowerCase()))) : 'Option';
      return `<label class="sf-variant"><span class="sf-variant-label">${esc(label)}</span><select data-act="variant:${l.key}" data-variant="${l.key}" aria-label="${esc(label)} for ${esc(it.name)}">
          ${vs.map(v => `<option value="${v.id}" ${v.id === it.id ? 'selected' : ''}>${esc(variantLabel(it, v))}</option>`).join('')}
        </select></label>`;
    }

    /* − qty + control. An add-on at 1 shows "Remove" on the minus button, which takes it out */
    function qtyStepper(l, addon) {
      const max = addon ? ADDON_MAX : 64;
      const down = addon && l.qty <= 1
        ? `<button type="button" data-act="qtydown:${l.key}" aria-label="Remove ${esc(l.item.name)}">−</button>`
        : `<button type="button" data-act="qtydown:${l.key}" aria-label="Fewer" ${l.qty <= 1 ? 'disabled' : ''}>−</button>`;
      return `<span class="sf-qty" role="group" aria-label="Quantity of ${esc(l.item.name)}">${down}
          <output aria-live="polite">${l.qty}</output>
          <button type="button" data-act="qtyup:${l.key}" aria-label="More" ${l.qty >= max ? 'disabled' : ''}>+</button></span>`;
    }
    /* Product name (new tab), SKU and an info icon whose tooltip explains the line */
    function lineHead(l) {
      const id = `${root.id || 'sf'}-line-${l.key}`;
      const name = `${esc(l.item.name)}${l.qty > 1 && !l.adjustable ? ` × ${l.qty}` : ''}`;
      const info = `<span class="sf-info"><button type="button" class="sf-info-btn" aria-label="Details about ${esc(l.item.name)}" aria-describedby="${id}-why">${icon('info')}</button><span class="sf-tip" role="tooltip" id="${id}-why">${esc(lineWhy(l))}</span></span>`;
      return `<a class="sf-line-name" target="_blank" rel="noopener" href="${esc(l.item.url)}">${name}<span class="visually-hidden"> (opens in a new tab)</span></a>
        <span class="sf-line-meta">${l.item.sku ? `SKU ${esc(l.item.sku)}` : ''}${info}</span>`;
    }

    /* A product in the recommended setup: always included, quantity adjustable where it makes sense */
    function lineRow(l) {
      const choices = l.options ? `<div class="sf-choices" role="radiogroup" aria-label="Choose your ${esc(ROLE_NAME[l.role] || 'camera')}">${l.options.map((o, i) => {
          const on = o.id === l.item.id;
          return `<button type="button" class="sf-choice" role="radio" aria-checked="${on}" data-act="choose:${l.key}:${o.id}">
            ${thumb(o)}<span class="sf-choice-text"><b>${esc(o.name)}</b>${skuTag(o)}${i === 0 ? '<em>Best match</em>' : ''}
            <span class="sf-choice-feats">${optionFeats(o).map(f => `<span>${esc(f)}</span>`).join('')}</span></span>
            <span class="sf-choice-price">${on ? fmt(o.price) : (() => {
              /* Other choices show what switching adds to the line, e.g. "+$53 CAD" */
              const d = (o.price - l.item.price) * l.qty;
              return d ? `<span class="sf-diff ${d > 0 ? 'is-up' : 'is-down'}">${esc(diffText(d))}</span>` : 'Same price';
            })()}</span></button>`;
        }).join('')}</div>` : '';
      return `<div class="sf-line${l.options ? ' has-choices' : ''}">
        <span class="sf-line-thumb">${thumb(l.item)}</span>
        <span class="sf-line-text">${lineHead(l)}${variantRow(l)}${l.adjustable ? qtyStepper(l, false) : ''}</span>
        <span class="sf-ap">${fmt(l.item.price * l.qty)}</span>${choices}</div>`;
    }

    /* An optional add-on: full contrast with an Add button, then a quantity stepper once added */
    function addonRow(l) {
      const control = l.on ? qtyStepper(l, true)
        : `<button type="button" class="sf-add" data-act="add:${l.key}" aria-label="Add ${esc(l.item.name)}">Add</button>`;
      return `<div class="sf-addon${l.on ? ' is-on' : ''}">
        <span class="sf-line-thumb">${thumb(l.item)}</span>
        <span class="sf-line-text">${lineHead(l)}</span>
        <span class="sf-addon-end"><b>+${fmt(l.item.price * l.qty)}</b>${control}</span></div>`;
    }

    const views = [
      () => `<section class="sf-intro">
          <div>
            ${INTRO.eyebrow ? `<h1 class="sf-eyebrow">${INTRO.eyebrow}</h1>` : ''}
            <h2 data-sf-heading tabindex="-1">${INTRO.title}</h2>
            ${INTRO.description ? `<p class="sf-lede">${INTRO.description}</p>` : ''}
            <div class="sf-intro-actions">
              <button type="button" class="sf-btn sf-btn--primary sf-btn--big" data-act="start">Start here</button>
              ${DATA.byo_url ? `<div class="sf-byo">
                <a class="sf-btn sf-btn--secondary sf-btn--big" target="_blank" rel="noopener" href="${esc(DATA.byo_url)}" aria-describedby="${root.id || 'sf'}-byo-hint">Build your own system<span class="visually-hidden"> (opens in a new tab)</span></a>
                <p class="sf-byo-hint" id="${root.id || 'sf'}-byo-hint">Know what you want? Pick a recorder, then mix and match cameras.</p>
              </div>` : ''}
            </div>
          </div>
          <figure>${planHTML('house', ZONES.house.map(z => z.id), false, 'is-intro')}</figure>
        </section>`,

      /* Only House and Condo are choices; Small business is a separate exit link below them */
      () => `<div class="sf-narrow">${stepHead(1, 'What are you protecting?', 'This sets which areas we show and which install options make sense.')}
          <div class="sf-opts sf-opts--two" role="radiogroup" aria-label="Property type">${Object.entries(PROPS).map(([id, o], i) => propCard(id, o, i)).join('')}</div>
          ${DATA.business_url ? `<a class="sf-biz-link" target="_blank" rel="noopener" href="${esc(DATA.business_url)}">
            <span><strong>Protecting a business?</strong> <span class="sf-biz-text">Explore professional-grade security cameras and systems at pro.lorex.com.</span></span>
            <span class="sf-biz-cta">Explore now <span aria-hidden="true">↗</span><span class="visually-hidden"> (opens in a new tab)</span></span>
          </a>` : ''}
        </div>`,

      () => {
        const sc = sceneOf();
        return stepHead(2, 'Which areas do you want to see?', 'Choose all the areas you’d like to monitor. Need more than one camera in an area? Use + to add more.') +
          `<div class="sf-zone-grid">
            ${planHTML(sc, state.zones, true)}
            <div>
              <div class="sf-zlist" role="group" aria-label="Areas" data-sf-zlist>${zoneListHTML()}</div>
              <p class="sf-zcount" data-sf-zcount aria-live="polite">${zoneSummary()}</p>
            </div>
          </div>`;
      },

      () => {
        const n = state.priorities.length;
        const sub = state.priorities.includes('detect') ? `<fieldset class="sf-sub"><legend>What should alerts recognize? <span>Optional</span></legend>
            <div class="sf-subchips">${Object.entries(DETECTIONS).map(([id, label]) => `<button type="button" data-act="det:${id}" aria-pressed="${state.detections.includes(id)}">${label}</button>`).join('')}</div></fieldset>` : '';
        return stepHead(3, 'What matters most to you?', 'Pick as many as you like. We’ll favour cameras that have them.') +
          `<p class="sf-counter" aria-live="polite"><b>${n}</b> picked${n ? '' : '. You can also skip this step.'}</p>
          <div class="sf-opts" role="group" aria-label="Priorities">${Object.entries(PRIOS).map(([id, o]) => optCard('prio', id, o, state.priorities.includes(id))).join('')}</div>${sub}
          <p class="sf-fine">Every Lorex setup records locally, so there are no monthly fees.</p>`;
      },

      () => {
        /* Shown for any property type: a house can be rented too */
        const rentalWired = ['coax', 'new', 'hybrid'].includes(state.install);
        /* Priorities come first, so the 24/7 recording note follows the install choice */
        const wifiNote = !state.priorities.includes('continuous') ? ''
          : state.install === 'wirefree' ? '<p class="sf-note">Battery cameras record motion clips, not 24/7. For 24/7 recording you’d need plug-in Wi-Fi cameras with a microSD card, so we’ll point that out in your results.</p>'
          : state.install === 'wifi' ? '<p class="sf-note">Battery cameras record motion clips only. We’ll favour plug-in Wi-Fi cameras, which record 24/7 to a microSD card.</p>' : '';
        return stepHead(4, 'How would you like to install?', 'Pick what fits your home. There\u2019s no wrong answer, and you can choose \u201cnot sure.\u201d') +
          `<div class="sf-opts sf-opts--props" role="group" aria-label="Install method">${Object.entries(INSTALL).map(([id, o]) => picCard('install', id, o, state.install === id, INSTALL_IMG[id])).join('')}</div>
          ${rentalWired ? '<p class="sf-note">Running or reusing cables may require landlord approval if you\'re renting—making battery-powered Wi-Fi cameras a great, hassle-free alternative.</p>' : ''}${wifiNote}
          <details class="sf-helper"><summary>How can I tell which cables I have?</summary>
            <div class="sf-helper-body">
              <figure>${cablePic('coax', '<path d="M4 40h30"/><rect x="34" y="28" width="22" height="24" rx="3"/><path d="M56 32h18v16H56M74 36h12v8H74"/><path d="M42 28v-6M48 28v-6"/>', 'Coaxial cable connector')}<b>Coaxial (round, twist-lock end)</b>Used by older analog systems. Choose \u201cexisting camera cables.\u201d</figure>
              <figure>${cablePic('ethernet', '<path d="M4 40h34"/><path d="M38 30h26l10 4v12l-10 4H38z"/><path d="M74 36h20v8H74M50 30v-6h10v6"/><path d="M78 36v8M83 36v8M88 36v8"/>', 'Ethernet cable connector')}<b>Ethernet (flat, clip end)</b>Used by IP cameras. Choose \u201cnew cables\u201d if you have these or will add them.</figure>
            </div>
          </details>`;
      },

      () => stepHead(5, 'What matters more: price or performance?', 'We\u2019ll cover every area you picked either way, and show you the other option on the results page.') +
        `<div class="sf-opts" role="group" aria-label="Budget">${Object.entries(BUDGET).map(([id, o]) => optCard('budget', id, o, state.budget === id)).join('')}</div>`,

      () => {
        const r = recommend();
        if (!r) {
          return `<div class="sf-empty"><header class="sf-qhead"><h1 data-sf-heading tabindex="-1">No matching products are available right now</h1><p class="sf-help">Nothing in the finder is in stock for your region at the moment. Try again later.</p></header><button type="button" class="sf-btn sf-btn--ghost" data-act="restart">Start over</button></div>`;
        }
        const chosen = [...r.lines, ...r.extras].filter(l => l.on);
        const items = chosen.reduce((t, l) => t + l.qty, 0);
        const total = fmt(totalOf(r));
        const picked = state.zones.map(zoneById).filter(Boolean);
        const areaNames = picked.map(z => z.label + (zoneCount(z.id) > 1 ? ` (${zoneCount(z.id)})` : ''));
        const prios = state.priorities.map(k => PRIOS[k].short);
        const sentence = `For your ${PROPS[state.property].label.toLowerCase()}: ${joinAnd(areaNames)}, ${INSTALL[state.install].short.toLowerCase()}${prios.length ? `, ${joinAnd(prios)}` : ''}.`;
        const why = whyList(r).map(([c, t, k]) => `<li class="${c === 'ok' ? '' : 'is-' + c}">${k ? `<strong>${esc(k)}:</strong> ` : ''}${esc(k ? t : cap(t))}${/[.!?]$/.test(t) ? '' : '.'}</li>`).join('');
        const extras = r.extras.length ? `<section class="sf-addons" aria-labelledby="${root.id || 'sf'}-addons"><h2 id="${root.id || 'sf'}-addons">Recommended add-ons</h2>${r.extras.map(addonRow).join('')}</section>` : '';
        const ALT_KIND = { best: 'Best match', budget: 'Budget-friendly', premium: 'Best performance' };
        const altRows = r.alts.map(({ kind, s: a }) => {
          const d = a.total - totalOf(r);
          return `<div class="sf-alt-row">
            <div class="sf-alt-text"><p class="sf-alt-kind">${ALT_KIND[kind]}</p><h3>${esc(approach(a))}</h3><p>${esc(altText(a, r))}</p></div>
            <div class="sf-alt-price"><b>${fmt(a.total)}</b><span class="${d > 0 ? 'is-up' : 'is-down'}">${d ? `${esc(diffText(d))} vs. current` : 'Same price'}</span></div>
            <button type="button" class="sf-btn-quiet" data-act="use:${kind === 'best' ? '' : a.id}" aria-label="Compare: switch to the ${ALT_KIND[kind].toLowerCase()} setup">Compare</button>
          </div>`;
        }).join('');
        const uncovered = new Set(r.uncovered.map(z => z.id));
        const chips = picked.map(z => `<span class="${uncovered.has(z.id) ? 'is-off' : ''}">${esc(z.label)}${zoneCount(z.id) > 1 ? ` (${zoneCount(z.id)})` : ''}${uncovered.has(z.id) ? '<span class="visually-hidden"> (not covered yet)</span>' : ''}</span>`).join('');
        const cartLabel = items ? `Check out ${plural(items, 'item')}` : 'Select products to buy';
        return `<div class="sf-rhead"><header class="sf-qhead"><h1 data-sf-heading tabindex="-1">Your recommended setup</h1><p class="sf-help">${esc(sentence)}</p></header></div>
          <div class="sf-result">
            <div class="sf-result-col">
              <article class="sf-result-main">
                <div class="sf-rm-head"><span class="sf-badge">${r.isPick ? 'Your pick' : 'Best match'}</span><span class="sf-typeline">${esc(approach(r))} · No monthly fees</span></div>
                <div class="sf-setup">${r.lines.map(lineRow).join('')}</div>
                <div class="sf-fit">
                  <h2>Why this fits your answers</h2>
                  <ul class="sf-why">${why}</ul>
                </div>
              </article>
              ${extras}
              <section class="sf-alts" aria-labelledby="${root.id || 'sf'}-alts">
                ${r.alts.length ? `<h2 id="${root.id || 'sf'}-alts">Other ways to cover the same areas</h2><div class="sf-alt-list">${altRows}</div>` : `<h2 id="${root.id || 'sf'}-alts" class="visually-hidden">More options</h2>`}
                <div class="sf-alt-links">
                  ${DATA.byo_url ? `<a target="_blank" rel="noopener" href="${esc(DATA.byo_url)}">Build your own system<span class="visually-hidden"> (opens in a new tab)</span></a>` : ''}
                  <button type="button" class="sf-link" data-act="copy">Copy link to this setup</button>
                </div>
              </section>
            </div>
            <aside class="sf-summary" aria-label="Order summary">
              <h2>Your setup</h2>
              <div class="sf-area-tags">${chips}</div>
              <ul class="sf-sum-list">${chosen.map(l => `<li><span>${esc(l.item.name)}${l.qty > 1 ? ` × ${l.qty}` : ''}</span><b>${fmt(l.item.price * l.qty)}</b></li>`).join('')}</ul>
              <div class="sf-total"><span>Total · ${plural(items, 'item')}</span><b>${total}</b></div>
              <button type="button" class="sf-btn sf-btn--primary sf-btn--wide sf-checkout" data-act="cart" ${items ? '' : 'disabled'}>${cartLabel}</button>
              ${SUMMARY_NOTE ? `<p class="sf-sum-note">${esc(SUMMARY_NOTE)}</p>` : ''}
              <div class="sf-sum-links">
                <div class="sf-sum-nav">
                  <button type="button" class="sf-sum-btn" data-act="back">${icon('back')}Back</button>
                  <button type="button" class="sf-sum-btn sf-sum-btn--grow" data-act="goto:1">${icon('sliders')}Change answers</button>
                </div>
                <button type="button" class="sf-sum-restart" data-act="restart">${icon('restart')}Start over</button>
              </div>
            </aside>
          </div>
          <div class="sf-mbar">
            <div><span>Total · ${plural(items, 'item')}</span><b>${total}</b></div>
            <button type="button" class="sf-btn sf-btn--primary sf-checkout" data-act="cart" ${items ? '' : 'disabled'}>Check out</button>
          </div>`;
      }
    ];

    /* ----- Rail and bar ----- */
    const canContinue = () => (state.step === 1 ? !!state.property : state.step === 2 ? state.zones.length > 0 : state.step === 4 ? !!state.install : true);
    /* Why Continue is unavailable, shown next to it */
    const NEXT_HINT = { 1: 'Choose a property type to continue.', 2: 'Pick at least one area to continue.', 4: 'Choose how you’d like to install to continue.' };
    function renderRail() {
      if (state.step === 0) { $rail.hidden = true; return; }
      $rail.hidden = false;
      $rail.innerHTML = `<ol>${STEPS.map((s, i) => {
        const n = i + 1, cls = state.step === n ? 'is-now' : state.step > n ? 'is-done' : '';
        return `<li class="${cls}"><button type="button" ${cls === 'is-done' ? `data-act="goto:${n}"` : 'tabindex="-1" aria-disabled="true"'} ${cls === 'is-now' ? 'aria-current="step"' : ''}><span>${n}</span>${s}</button></li>`;
      }).join('')}</ol>`;
    }
    function chipsHTML() {
      const c = [];
      if (state.property) c.push(PROPS[state.property].label);
      if (state.zones.length) c.push(plural(state.zones.length, 'area'));
      if (state.priorities.length) c.push(state.priorities.length === 1 ? '1 priority' : `${state.priorities.length} priorities`);
      if (state.install) c.push(INSTALL[state.install].short);
      if (BUDGET[state.budget]) c.push(BUDGET[state.budget].short || BUDGET[state.budget].label);
      return c.map(t => `<span class="sf-chip">${esc(t)}</span>`).join('');
    }
    function renderBar() {
      if (state.step === 0 || state.step === 6) { $bar.hidden = true; return; }
      $bar.hidden = false;
      let label = 'Continue';
      if (state.step === 3 && !state.priorities.length) label = 'Skip';
      if (state.step === 5) label = 'See my setup';
      $bar.innerHTML = `<div class="sf-bar-in">
        <button type="button" class="sf-btn sf-btn--ghost" data-act="back">Back</button>
        <div class="sf-chips" aria-label="Your answers so far">${chipsHTML()}</div>
        <button type="button" class="sf-btn sf-btn--primary" data-act="next" aria-disabled="${!canContinue()}" ${canContinue() ? '' : `aria-describedby="${root.id || 'sf'}-next-hint"`}>${label}</button>
        ${canContinue() ? '' : `<span class="sf-bar-hint" id="${root.id || 'sf'}-next-hint">${NEXT_HINT[state.step] || ''}</span>`}
      </div>`;
    }

    /* ----- Render ----- */
    function render() {
      const focusKey = root.contains(document.activeElement) ? document.activeElement.dataset.act : null;
      $main.innerHTML = views[state.step]();
      $main.classList.toggle('is-results', state.step === 6);
      renderRail(); renderBar(); writeHash();
      if (state.step !== lastStep) {
        const first = lastStep === -1;
        lastStep = state.step;
        if (!first) {
          root.scrollIntoView({ block: 'start' });
          const hd = root.querySelector('[data-sf-heading]');
          if (hd) hd.focus({ preventScroll: true });
        }
      } else if (focusKey) {
        const el = root.querySelector(`[data-act="${focusKey}"]`);
        if (el && !el.disabled) el.focus({ preventScroll: true });
      }
    }
    const go = n => { state.step = Math.max(0, Math.min(6, n)); render(); };

    function toggleZone(id) {
      const i = state.zones.indexOf(id);
      if (i >= 0) { state.zones.splice(i, 1); delete state.counts[id]; } else state.zones.push(id);
      clearPick();
      const on = state.zones.includes(id);
      $main.querySelectorAll(`.sf-pin[data-act="zone:${id}"]`).forEach(el => el.setAttribute('aria-pressed', on));
      const cone = $main.querySelector(`[data-cone="${id}"]`); if (cone) cone.classList.toggle('on', on);
      const dot = $main.querySelector(`[data-dot="${id}"]`); if (dot) dot.classList.toggle('on', on);
      /* Keep focus on whichever control was used (map pin or list checkbox) */
      const fromList = document.activeElement && document.activeElement.type === 'checkbox';
      refreshZones(fromList ? `zone:${id}` : null);
    }

    /* ----- URL hash state (shareable, survives refresh) ----- */
    function writeHash() {
      const q = new URLSearchParams();
      if (state.step) q.set('s', state.step);
      if (state.property) q.set('p', state.property);
      if (state.zones.length) q.set('z', state.zones.join(','));
      const zc = state.zones.filter(id => zoneCount(id) > 1).map(id => `${id}~${zoneCount(id)}`);
      if (zc.length) q.set('zc', zc.join(','));
      if (state.install) q.set('i', state.install);
      if (state.priorities.length) q.set('pr', state.priorities.join(','));
      if (state.detections.length) q.set('dt', state.detections.join(','));
      if (state.budget) q.set('b', state.budget);
      if (state.pick) q.set('pick', state.pick);
      const vs = Object.entries(state.variant).map(([k, id]) => `${k}~${id}`);
      if (vs.length) q.set('v', vs.join(','));
      const h = q.toString();
      try { history.replaceState(null, '', h ? '#' + h : location.pathname + location.search); } catch (e) { /* ignore */ }
    }
    function readHash() {
      try {
        const q = new URLSearchParams(location.hash.slice(1));
        if (PROPS[q.get('p')]) state.property = q.get('p');
        const valid = zonesOf().map(z => z.id);
        state.zones = (q.get('z') || '').split(',').filter(z => valid.includes(z));
        (q.get('zc') || '').split(',').forEach(p => {
          const [id, n] = p.split('~');
          if (state.zones.includes(id) && /^\d+$/.test(n || '')) state.counts[id] = Math.min(ZONE_MAX, Math.max(1, +n));
        });
        if (INSTALL[q.get('i')]) state.install = q.get('i');
        state.priorities = (q.get('pr') || '').split(',').filter(p => PRIOS[p]);
        state.detections = (q.get('dt') || '').split(',').filter(d => DETECTIONS[d]);
        if (BUDGET[q.get('b')]) state.budget = q.get('b');
        if (/^[\dx.]+$/.test(q.get('pick') || '')) state.pick = q.get('pick');
        (q.get('v') || '').split(',').forEach(p => {
          const [k, id] = p.split('~');
          if (/^[\w-]+$/.test(k || '') && /^\d+$/.test(id || '')) state.variant[k] = id;
        });
        const s = parseInt(q.get('s'), 10) || 0;
        const max = !state.property ? 1 : !state.zones.length ? 2 : !state.install ? 4 : 6;
        state.step = Math.max(0, Math.min(s, max));
      } catch (e) { /* ignore */ }
    }

    /* ----- Toast ----- */
    let tt;
    function toast(msg) {
      $toast.textContent = msg; $toast.classList.add('is-shown');
      clearTimeout(tt); tt = setTimeout(() => $toast.classList.remove('is-shown'), 2800);
    }

    /* ----- Cart ----- */
    async function addToCart(btn) {
      const r = recommend();
      if (!r) return;
      const setupName = approach(r);
      const items = [...r.lines, ...r.extras].filter(l => l.on).map(l => ({ id: l.item.variant_id, quantity: l.qty, properties: lineProperties(l, setupName) }));
      if (!items.length) return;
      const label = btn.textContent;
      btn.disabled = true; btn.textContent = 'Going to checkout\u2026';
      try {
        /* Start from an empty cart so checkout only has this setup */
        const cleared = await fetch(DATA.cart_clear_url || '/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
        if (!cleared.ok) throw new Error('Your cart couldn\u2019t be updated. Please try again.');
        const res = await fetch(DATA.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'One or more items couldn\u2019t be added to the cart.');
        }
        window.location.href = DATA.checkout_url || '/checkout';
      } catch (e) {
        toast(e.message);
        btn.disabled = false; btn.textContent = label;
      }
    }

    /* ----- Events ----- */
    root.addEventListener('click', e => {
      const t = e.target.closest('[data-act]');
      if (!t || t.disabled || !root.contains(t)) return;
      const [act, val = '', extra = ''] = t.dataset.act.split(':');
      switch (act) {
        case 'start': go(1); break;
        case 'choose': state.choice[val] = extra; delete state.variant[val]; render(); break;
        case 'prop': {
          const before = sceneOf(); state.property = val;
          if (sceneOf() !== before) { state.zones = []; state.counts = {}; }
          clearPick(); render(); break;
        }
        case 'zone': toggleZone(val); break;
        case 'zqty': {
          state.counts[val] = Math.max(1, Math.min(ZONE_MAX, zoneCount(val) + (+extra || 0)));
          clearPick(); refreshZones(t.dataset.act); break;
        }
        case 'install': state.install = val; clearPick(); render(); break;
        case 'prio': {
          const i = state.priorities.indexOf(val);
          if (i >= 0) { state.priorities.splice(i, 1); if (val === 'detect') state.detections = []; }
          else state.priorities.push(val);
          clearPick(); render(); break;
        }
        case 'det': {
          const i = state.detections.indexOf(val);
          if (i >= 0) state.detections.splice(i, 1); else state.detections.push(val);
          clearPick(); render(); break;
        }
        case 'budget': state.budget = val; clearPick(); render(); break;
        case 'next': if (canContinue()) go(state.step + 1); break;
        case 'back': go(state.step - 1); break;
        case 'goto': go(+val); break;
        /* Add-ons: Add puts one in at its suggested quantity; minus at 1 takes it out again */
        case 'add': {
          state.sel[val] = true; render();
          const more = $main.querySelector(`[data-act="qtyup:${val}"]`); if (more) more.focus({ preventScroll: true });
          break;
        }
        case 'qtyup':
        case 'qtydown': {
          const r = recommend(); if (!r) break;
          const l = [...r.lines, ...r.extras].find(x => x.key === val); if (!l) break;
          const addon = r.extras.includes(l);
          if (addon && act === 'qtydown' && l.qty <= 1) {
            state.sel[val] = false; delete state.qty[val];
            render();
            const add = $main.querySelector(`[data-act="add:${val}"]`); if (add) add.focus({ preventScroll: true });
            break;
          }
          state.qty[val] = Math.max(1, Math.min(addon ? ADDON_MAX : 64, l.qty + (act === 'qtyup' ? 1 : -1)));
          render(); break;
        }
        case 'use': state.pick = val || null; state.sel = {}; state.variant = {}; render(); toast(val ? 'Setup switched.' : 'Back to the best match.'); break;
        case 'cart': addToCart(t); break;
        case 'copy':
          (navigator.clipboard ? navigator.clipboard.writeText(location.href) : Promise.reject())
            .then(() => toast('Link copied'))
            .catch(() => toast('Copy this page\u2019s address to share it'));
          break;
        case 'restart':
          Object.assign(state, { property: null, zones: [], counts: {}, install: null, priorities: [], detections: [], budget: null, pick: null, sel: {}, variant: {} });
          go(0); break;
      }
    });
    root.addEventListener('keydown', e => {
      /* Escape hides an open product tooltip */
      if (e.key === 'Escape' && document.activeElement && document.activeElement.matches('.sf-info-btn')) document.activeElement.blur();
      /* Arrow keys move the choice within a radio group (property type, indoor camera options) */
      const radio = e.target.closest && e.target.closest('[role="radio"]');
      const group = radio && radio.closest('[role="radiogroup"]');
      if (group && /^Arrow(Up|Down|Left|Right)$/.test(e.key)) {
        const all = [...group.querySelectorAll('[role="radio"]')];
        const next = all[(all.indexOf(radio) + (/Right|Down/.test(e.key) ? 1 : -1) + all.length) % all.length];
        e.preventDefault();
        next.focus(); next.click();
      }
    });
    /* Variant dropdowns: price, image, name and coverage update on change */
    root.addEventListener('change', e => {
      const s = e.target.closest('select[data-variant]');
      if (!s || !root.contains(s)) return;
      state.variant[s.dataset.variant] = s.value;
      render();
      toast('Option updated.');
    });

    /* Dev helper: add ?calibrate to the URL, then click the map to log coordinates */
    if (/[?&]calibrate\b/.test(location.search)) {
      root.addEventListener('click', e => {
        const plan = e.target.closest('.sf-plan'); if (!plan) return;
        const b = plan.getBoundingClientRect();
        console.log(`[${Math.round((e.clientX - b.left) / b.width * 600)}, ${Math.round((e.clientY - b.top) / b.height * 420)}]`);
      }, true);
      console.info('System finder calibrate mode: click the map to log [x, y].', { systems: P, standalone: ACC });
    }

    readHash();
    render();

    /* Correct bundle camera counts in the background; refresh the results if they're showing */
    const bundleIds = bundleCandidates(DATA);
    if (bundleIds.length) {
      fetchBundleCams(bundleIds).then(over => {
        if (!Object.keys(over).length) return;
        ({ systems: P, standalone: ACC } = buildCatalog(DATA, over));
        setFlags();
        if (state.step === 6) render();
      }).catch(e => console.warn('System finder: bundle components unavailable', e));
    }
  }

  const boot = scope => scope.querySelectorAll('[data-system-finder]').forEach(init);
  boot(document);
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
